<template>
  <div
    class="min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50 flex items-stretch justify-center relative overflow-hidden transition-colors duration-500">

    <div class="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:py-8 relative z-10">

      <header
        class="flex flex-col gap-4 mb-6 lg:mb-10 lg:flex-row lg:items-center lg:justify-between animate-fade-in-down">
        <div class="flex items-center gap-3 lg:gap-4">
          <button type="button" @click="goBack"
            class="group flex h-9 w-9 lg:h-10 lg:w-10 items-center justify-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-500 transition-all duration-300 hover:scale-110 hover:border-zinc-300 dark:hover:border-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100 shadow-sm hover:shadow-md">
            <span
              class="material-symbols-rounded text-[18px] lg:text-[20px] transition-transform group-hover:-translate-x-0.5">
              arrow_back
            </span>
          </button>
          <div>
            <div class="flex items-center gap-2 mb-1">
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-[9px] lg:text-[10px] uppercase tracking-wider font-bold text-zinc-500 dark:text-zinc-400">
                Studio
              </span>
              <span class="h-1 w-1 rounded-full bg-zinc-300 dark:bg-zinc-700"></span>
              <span class="text-[10px] lg:text-[11px] text-zinc-400 dark:text-zinc-500 tracking-wide uppercase">Color
                Engineering</span>
            </div>
            <h1
              class="text-2xl lg:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white flex items-center gap-3">
              {{ project.name || 'Untitled Palette' }}
              <button
                class="opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-zinc-600">
                <span class="material-symbols-rounded text-[18px]">edit</span>
              </button>
            </h1>
          </div>
        </div>

        <div class="flex items-center gap-3">

          <router-link to="/dashboard/hub"
            class="h-7 lg:h-8 w-7 lg:w-8 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:border-zinc-300 dark:hover:border-zinc-700 transition-all shadow-sm hover:shadow-md group"
            title="Tutorial Hub">
            <span
              class="material-symbols-rounded text-[16px] lg:text-[18px] transition-transform duration-300 group-hover:rotate-12">school</span>
          </router-link>

          <div
            class="h-7 lg:h-8 px-3 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-[10px] lg:text-xs font-medium text-zinc-500">
            Auto-save active
          </div>

        </div>
      </header>

      <div class="grid gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-[1fr_480px] xl:grid-cols-[1fr_560px] items-start">

        <section class="flex flex-col gap-6 animate-fade-in-up" style="animation-delay: 100ms;">

          <div
            class="rounded-[24px] lg:rounded-[32px] bg-white/70 dark:bg-zinc-900/70 backdrop-blur-2xl border border-white/20 dark:border-zinc-800/50 shadow-xl dark:shadow-2xl dark:shadow-black/50 p-5 lg:p-8 flex flex-col gap-6 transition-all duration-300 hover:border-zinc-300/50 dark:hover:border-zinc-700/50">

            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 class="text-base lg:text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                  <span class="material-symbols-rounded text-emerald-500 text-[20px]">colorize</span>
                  Chromatics
                </h2>
                <p class="text-xs lg:text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                  Define your spectrum manually or use AI.
                </p>
              </div>

              <div class="flex items-center gap-2 w-full sm:w-auto">
                <button type="button" @click="openPaletteModal"
                  class="flex-1 sm:flex-none justify-center h-9 px-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-all active:scale-95 flex items-center gap-2">
                  <span class="material-symbols-rounded text-[16px]">palette</span>
                  Presets
                </button>
                <button type="button" @click="generateWithAI"
                  class="flex-1 sm:flex-none justify-center group relative h-9 px-5 rounded-xl bg-zinc-900 dark:bg-zinc-50 text-white dark:text-zinc-950 text-xs font-semibold shadow-lg shadow-zinc-500/20 hover:shadow-zinc-500/40 transition-all active:scale-95 overflow-hidden flex items-center gap-2">
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-indigo-500 opacity-0 group-hover:opacity-10 dark:group-hover:opacity-20 transition-opacity">
                  </div>
                  <span
                    class="material-symbols-rounded text-[16px] transition-transform group-hover:rotate-12">auto_awesome</span>
                  Generate
                </button>
              </div>
            </div>

            <div class="space-y-3 max-h-[400px] lg:max-h-[460px] overflow-y-auto pr-1 lg:pr-2 custom-scroll">
              <TransitionGroup name="list" tag="div" class="space-y-3">
                <div v-for="(color, idx) in colors" :key="color.id"
                  class="group relative flex items-center gap-3 lg:gap-4 p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-950/40 border border-zinc-100 dark:border-zinc-800/50 hover:border-zinc-300 dark:hover:border-zinc-700 transition-all duration-200 hover:shadow-md">

                  <div class="relative flex-shrink-0">
                    <input v-model="color.hex" type="color" @input="onHexChange(color)"
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                    <div
                      class="h-12 w-12 lg:h-14 lg:w-14 rounded-xl shadow-inner ring-1 ring-black/5 dark:ring-white/10 transition-transform group-hover:scale-105"
                      :style="{ backgroundColor: color.hex }"></div>
                  </div>

                  <div
                    class="flex-1 min-w-0 grid grid-cols-[1fr_90px] lg:grid-cols-[1fr_120px] gap-3 lg:gap-4 items-center">
                    <div class="space-y-0.5 lg:space-y-1">
                      <label
                        class="text-[9px] lg:text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider block">
                        Tone {{ idx + 1 }}
                      </label>
                      <input v-model="color.name" type="text" placeholder="Color Name"
                        class="w-full bg-transparent border-none p-0 text-sm font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:ring-0 truncate" />
                    </div>

                    <div class="space-y-0.5 lg:space-y-1">
                      <label
                        class="text-[9px] lg:text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider block">
                        HEX
                      </label>
                      <input v-model="color.hex" type="text" @blur="onHexChange(color)"
                        class="w-full bg-zinc-100 dark:bg-zinc-900 rounded-lg px-2 py-1 text-[11px] lg:text-xs font-mono text-zinc-600 dark:text-zinc-300 border-none focus:ring-1 focus:ring-zinc-400 dark:focus:ring-zinc-600 uppercase text-center" />
                    </div>
                  </div>

                  <button type="button" @click="removeColor(color.id)" v-if="colors.length > 2"
                    class="lg:opacity-0 lg:group-hover:opacity-100 transition-opacity p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg">
                    <span class="material-symbols-rounded text-[18px]">close</span>
                  </button>
                </div>
              </TransitionGroup>
            </div>

            <div
              class="pt-4 border-t border-zinc-100 dark:border-zinc-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <button type="button" @click="addColor"
                class="w-full sm:w-auto justify-center flex items-center gap-2 text-xs font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors py-2 sm:py-0">
                <div class="h-6 w-6 rounded-full border border-dashed border-current flex items-center justify-center">
                  <span class="material-symbols-rounded text-[14px]">add</span>
                </div>
                Add tone
              </button>

              <div class="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <button type="button" @click="saveAndOpenFolderModal" :disabled="isSaving"
                  class="w-full sm:w-auto h-10 px-5 rounded-xl border border-zinc-200 dark:border-zinc-800 text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-all flex items-center justify-center gap-2">
                  <span class="material-symbols-rounded text-[18px]">folder_open</span>
                  Save to..
                </button>
                <button type="button" @click="saveProject" :disabled="isSaving"
                  class="w-full sm:w-auto h-10 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-semibold shadow-lg shadow-emerald-500/20 transition-all active:scale-95 flex items-center justify-center gap-2">
                  <span v-if="!isSaving" class="material-symbols-rounded text-[18px]">save</span>
                  <span v-if="!isSaving">Save Changes</span>
                  <span v-else>Saving...</span>
                </button>
              </div>
            </div>

          </div>
        </section>

        <aside class="flex flex-col gap-6 animate-fade-in-up" style="animation-delay: 200ms;">

          <div
            class="rounded-[24px] lg:rounded-[32px] bg-zinc-900 text-white p-1 shadow-2xl dark:shadow-black/80 ring-1 ring-white/10">
            <div class="relative bg-zinc-950 rounded-[20px] lg:rounded-[28px] overflow-hidden group"
              :style="{ aspectRatio: ['photo-card-stack', 'spotlight'].includes(currentTemplate?.id) ? '4 / 5' : '1 / 1' }">

              <div v-if="!currentTemplate"
                class="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 p-8 text-center">
                <span class="material-symbols-rounded text-4xl mb-3 opacity-50">view_quilt</span>
                <p class="text-sm">Select a template to preview</p>
              </div>

              <div v-else class="relative w-full h-full p-4 sm:p-8 flex items-center justify-center overflow-hidden">
                <div class="absolute inset-0 bg-zinc-950"></div>
                <div
                  class="absolute top-[-20%] right-[-20%] w-[80%] h-[80%] rounded-full bg-purple-500/25 blur-[90px] pointer-events-none">
                </div>
                <div
                  class="absolute bottom-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-emerald-500/20 blur-[90px] pointer-events-none">
                </div>
                <div
                  class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none">
                </div>

                <div
                  class="relative z-10 w-full h-full shadow-2xl rounded-[20px] lg:rounded-[32px] overflow-hidden transform transition-transform duration-500 group-hover:scale-[1.02] ring-1 ring-white/10">
                  <component :is="currentTemplate.component" :colors="previewColorsForTemplate(currentTemplate.id)" />
                </div>
              </div>

              <div
                class="absolute bottom-4 lg:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity duration-300 lg:translate-y-2 lg:group-hover:translate-y-0 z-20 w-max">
                <button @click="openTemplateModal"
                  class="h-9 px-4 bg-white/10 backdrop-blur-md hover:bg-white/20 rounded-full text-xs font-medium border border-white/10 flex items-center gap-2 shadow-lg text-white">
                  <span class="material-symbols-rounded text-[16px]">grid_view</span>
                  Change Layout
                </button>
                <button @click="exportPalettePng" :disabled="!currentTemplate"
                  class="h-9 w-9 bg-white text-zinc-950 rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-lg">
                  <span class="material-symbols-rounded text-[18px]">download</span>
                </button>
              </div>

            </div>
          </div>

          <div
            class="rounded-2xl lg:rounded-3xl bg-white/50 dark:bg-zinc-900/50 p-4 lg:p-6 border border-zinc-200/50 dark:border-zinc-800/50 backdrop-blur-sm">
            <h3 class="text-[10px] lg:text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 lg:mb-4">Quick
              Actions</h3>
            <div class="grid grid-cols-2 gap-3">
              <button @click="generateWithAI"
                class="h-16 lg:h-20 rounded-xl lg:rounded-2xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 hover:border-violet-500/40 transition-all flex flex-col items-center justify-center gap-1 lg:gap-2 group">
                <span
                  class="material-symbols-rounded text-violet-500 text-xl lg:text-2xl group-hover:scale-110 transition-transform">auto_awesome</span>
                <span
                  class="text-[9px] lg:text-[10px] font-medium text-violet-700 dark:text-violet-300">Regenerate</span>
              </button>
              <button @click="openTemplateModal"
                class="h-16 lg:h-20 rounded-xl lg:rounded-2xl bg-zinc-100/50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-zinc-700/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all flex flex-col items-center justify-center gap-1 lg:gap-2 group">
                <span
                  class="material-symbols-rounded text-zinc-500 text-xl lg:text-2xl group-hover:scale-110 transition-transform">view_carousel</span>
                <span class="text-[9px] lg:text-[10px] font-medium text-zinc-600 dark:text-zinc-400">Layouts</span>
              </button>
            </div>
          </div>
        </aside>

      </div>
    </div>

    <div v-if="isTemplateModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-zinc-950/60 backdrop-blur-sm"
      @click.self="closeTemplateModal">

      <div
        class="w-full max-w-3xl max-h-[85vh] rounded-[24px] lg:rounded-[32px] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex flex-col shadow-2xl animate-scale-in overflow-hidden">

        <div
          class="p-5 lg:p-6 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between flex-shrink-0">
          <div>
            <p
              class="text-[10px] lg:text-[11px] uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400 font-bold">
              Layout Templates
            </p>
            <h2 class="text-base lg:text-lg font-bold text-zinc-900 dark:text-white mt-1">
              Choose how to export
            </h2>
          </div>
          <button type="button" @click="closeTemplateModal"
            class="h-8 w-8 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex items-center justify-center">
            <span class="material-symbols-rounded text-[20px]">close</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-5 lg:p-6 bg-zinc-50/50 dark:bg-zinc-950/50 custom-scroll">

          <div class="grid gap-4 lg:gap-5 grid-cols-1 sm:grid-cols-2">
            <button v-for="tpl in templates" :key="tpl.id" type="button" @click="chooseTemplate(tpl.id)"
              class="group relative flex flex-col p-4 rounded-2xl border text-left transition-all duration-300 bg-white dark:bg-zinc-900 hover:shadow-xl hover:-translate-y-1"
              :class="selectedTemplateId === tpl.id ? 'ring-2 ring-violet-500 border-transparent shadow-violet-500/20' : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700'">

              <div class="flex items-center justify-between gap-3 mb-2">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="material-symbols-rounded text-[18px] text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                    view_quilt
                  </span>
                  <p class="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate">
                    {{ tpl.name }}
                  </p>
                </div>
                <span
                  class="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 whitespace-nowrap flex-shrink-0 font-medium">
                  {{ tpl.aspect }}
                </span>
              </div>

              <p class="text-[11px] mb-4 text-zinc-500 dark:text-zinc-400 line-clamp-2 h-8 leading-relaxed">
                {{ tpl.description }}
              </p>

              <div class="relative w-full mt-auto">
                <div
                  class="w-full rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-800/50 group-hover:border-zinc-200 dark:group-hover:border-zinc-700 transition-colors select-none pointer-events-none"
                  :style="{ aspectRatio: ['photo-card-stack', 'spotlight'].includes(tpl.id) ? '4 / 5' : '1 / 1' }">
                  <component :is="tpl.component" :colors="previewColorsForTemplate(tpl.id)" />
                </div>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>

    <div v-if="isPaletteModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-zinc-950/60 backdrop-blur-sm transition-opacity"
      @click.self="closePaletteModal">

      <div
        class="w-full max-w-4xl bg-white dark:bg-zinc-900 rounded-[24px] lg:rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-scale-in">

        <div
          class="p-5 lg:p-6 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 class="text-base lg:text-lg font-bold text-zinc-900 dark:text-white">Color Harmonies</h3>
            <p class="text-xs lg:text-sm text-zinc-500">Curated combinations to jumpstart your design.</p>
          </div>
          <button @click="closePaletteModal"
            class="h-8 w-8 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors">
            <span class="material-symbols-rounded text-[20px]">close</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-5 lg:p-6 bg-zinc-50/50 dark:bg-zinc-950/50 custom-scroll">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <button v-for="combo in paletteCombos" :key="combo.id" @click="applyPaletteCombo(combo)"
              class="group flex flex-col rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 hover:border-violet-500/50 hover:shadow-lg transition-all text-left">
              <div class="flex items-center justify-between mb-3">
                <span class="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{{ combo.name }}</span>
                <span class="text-[10px] font-medium text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded-full">
                  {{ combo.tag || 'Combo' }}
                </span>
              </div>
              <div class="h-14 lg:h-16 w-full rounded-xl overflow-hidden flex">
                <div v-for="color in combo.colors" :key="color" :style="{ backgroundColor: color }"
                  class="flex-1 h-full"></div>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>

    <div v-if="isFolderModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center px-4 bg-zinc-950/60 backdrop-blur-sm transition-opacity"
      @click.self="closeFolderModal">
      <div
        class="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-[24px] lg:rounded-[32px] shadow-2xl overflow-hidden animate-scale-in p-5 lg:p-6">
        <h3 class="text-base lg:text-lg font-bold text-zinc-900 dark:text-white mb-1">Save to Collection</h3>
        <p class="text-xs lg:text-sm text-zinc-500 mb-6">Select a folder to organize your palette.</p>

        <div v-if="isFoldersLoading" class="py-8 text-center text-zinc-400 text-sm">Loading folders...</div>
        <div v-else-if="!folders.length" class="py-8 text-center text-zinc-400 text-sm">No folders found.</div>
        <div v-else class="space-y-2 max-h-[300px] overflow-y-auto custom-scroll pr-1 mb-6">
          <button v-for="folder in folders" :key="folder.id" @click="selectedFolderId = folder.id"
            class="w-full flex items-center gap-3 p-3 rounded-xl border transition-all"
            :class="selectedFolderId === folder.id ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-500 text-violet-700 dark:text-violet-300' : 'bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-zinc-300 dark:hover:border-zinc-700'">
            <span class="material-symbols-rounded"
              :class="selectedFolderId === folder.id ? 'text-violet-500' : 'text-zinc-400'">folder</span>
            <span class="text-sm font-medium">{{ folder.name }}</span>
          </button>
        </div>

        <div class="flex justify-end gap-3">
          <button @click="closeFolderModal"
            class="px-4 py-2 rounded-xl text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">Cancel</button>
          <button @click="confirmSaveToFolder" :disabled="!selectedFolderId || isSavingToFolder"
            class="px-5 lg:px-6 py-2 rounded-xl bg-emerald-500 text-zinc-950 text-xs font-bold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {{ isSavingToFolder ? 'Saving...' : 'Confirm Save' }}
          </button>
        </div>
      </div>
    </div>

    <div class="fixed -left-[9999px] -top-[9999px]" aria-hidden="true">
      <div ref="exportRef" class="relative flex items-center justify-center export-typography export-canvas" :style="{
        width: EXPORT_WIDTH + 'px',
        height: exportHeight + 'px',
        background: 'transparent'
      }">
        <div class="w-full h-full overflow-hidden">
          <component v-if="currentTemplate" :is="currentTemplate.component"
            :colors="previewColorsForTemplate(currentTemplate.id)" :is-export="true" />
        </div>
        <div v-if="!['photo-card-stack', 'spotlight'].includes(currentTemplate?.id)"
          class="absolute bottom-8 right-10 text-[12px] text-zinc-400 dark:text-zinc-500 tracking-[0.14em] uppercase">
          NexGridCo
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, defineComponent, h, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import html2canvas from 'html2canvas'
import authService from '@/service/authService'

const route = useRoute()
const router = useRouter()

const project = reactive({
  id: null,
  name: '',
  description: ''
})

const colors = ref([])
const selectedTemplateId = ref(null)
const isTemplateModalOpen = ref(false)
const isPaletteModalOpen = ref(false)
const isLoading = ref(false)
const isSaving = ref(false)

const isFolderModalOpen = ref(false)
const isFoldersLoading = ref(false)
const isSavingToFolder = ref(false)
const folders = ref([])
const selectedFolderId = ref(null)

const EXPORT_WIDTH = 1080
const exportHeight = computed(() => ['photo-card-stack', 'spotlight'].includes(selectedTemplateId.value) ? 1350 : 1080)
const exportRef = ref(null)
const photoStackExportBackground = ref('')

const NAME_ADJECTIVES = {
  bright: ['Electric', 'Vivid', 'Hyper', 'Neon', 'Sonic', 'Ultra', 'Luminous', 'Active', 'Hot', 'Laser', 'Plasma', 'Flux', 'Radio'],
  dark: ['Midnight', 'Deep', 'Noir', 'Obsidian', 'Ink', 'Night', 'Carbon', 'Abyssal', 'Hidden', 'Void', 'Vampire', 'Eclipse', 'Black'],
  pale: ['Soft', 'Ice', 'Pale', 'Misty', 'Air', 'Lite', 'Frost', 'Silk', 'Baby', 'Cool', 'Whisper', 'Ghost', 'Paper', 'Snow'],
  desaturated: ['Slate', 'Grey', 'Ash', 'Stone', 'Steel', 'Iron', 'Muted', 'Fog', 'Matte', 'Dusty', 'Urban', 'Concrete', 'Smoke'],
  general: ['True', 'Royal', 'Pure', 'Master', 'Classic', 'Prime', 'Grand', 'Noble', 'Real', 'Clean', 'Wild', 'Magic', 'Epic']
}

const NAME_NOUNS = {
  red: ['Crimson', 'Ruby', 'Rose', 'Mars', 'Brick', 'Berry', 'Lipstick', 'Cherry', 'Fire', 'Heat', 'Blood', 'Jam', 'Chili', 'Velvet'],
  orange: ['Amber', 'Sunset', 'Tiger', 'Clay', 'Spice', 'Tangelo', 'Rust', 'Coral', 'Pumpkin', 'Flare', 'Mango', 'Peach', 'Ginger'],
  yellow: ['Gold', 'Sun', 'Lemon', 'Daisy', 'Volt', 'Banana', 'Honey', 'Butter', 'Ray', 'Corn', 'Yolk', 'Cheese', 'Taxi'],
  green: ['Emerald', 'Sage', 'Mint', 'Forest', 'Jade', 'Lime', 'Moss', 'Pine', 'Basil', 'Leaf', 'Toxic', 'Alien', 'Slime', 'Fern'],
  teal: ['Teal', 'Aqua', 'Ocean', 'Cyan', 'Turquoise', 'Lagoon', 'Marine', 'Wave', 'Tide', 'Abyss', 'Arctic', 'Ice', 'Pool'],
  blue: ['Azure', 'Cobalt', 'Sky', 'Blue', 'Indigo', 'Sapphire', 'Denim', 'Ice', 'Royal', 'Navy', 'Galaxy', 'Winter', 'Ink'],
  purple: ['Violet', 'Plum', 'Mauve', 'Purple', 'Lavender', 'Grape', 'Amethyst', 'Orchid', 'Iris', 'Haze', 'Magic', 'Dream', 'Galaxy'],
  pink: ['Fuchsia', 'Pink', 'Blush', 'Magenta', 'Flamingo', 'Rose', 'Cotton', 'Candy', 'Punch', 'Rouge', 'Bubblegum', 'Taffy', 'Love'],
  grayscale: ['Graphite', 'Silver', 'Slate', 'Granite', 'Smoke', 'Metal', 'Chrome', 'Onyx', 'Lead', 'Iron', 'Cloud', 'Shadow', 'Moon']
}

function normalizeHex(hex) {
  if (!hex) return '#000000'
  let h = hex.trim()
  if (!h.startsWith('#')) h = '#' + h
  if (h.length === 4) {
    h = '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3]
  }
  return h.toUpperCase()
}

function generateColorName(hex) {
  const hHex = normalizeHex(hex).replace('#', '')
  const r = parseInt(hHex.substring(0, 2), 16)
  const g = parseInt(hHex.substring(2, 4), 16)
  const b = parseInt(hHex.substring(4, 6), 16)

  const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255
  const max = Math.max(rNorm, gNorm, bNorm), min = Math.min(rNorm, gNorm, bNorm)
  let hue = 0, sat = 0, lum = (max + min) / 2
  const d = max - min

  if (d !== 0) {
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case rNorm: hue = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0); break
      case gNorm: hue = (bNorm - rNorm) / d + 2; break
      case bNorm: hue = (rNorm - gNorm) / d + 4; break
    }
    hue *= 60
  }

  let family = 'grayscale'
  if (sat > 0.05) {
    if (hue < 15 || hue >= 345) family = 'red'
    else if (hue < 40) family = 'orange'
    else if (hue < 65) family = 'yellow'
    else if (hue < 160) family = 'green'
    else if (hue < 200) family = 'teal'
    else if (hue < 260) family = 'blue'
    else if (hue < 300) family = 'purple'
    else family = 'pink'
  }

  let adjCategory = 'general'
  if (sat < 0.15) adjCategory = 'desaturated'
  else if (lum < 0.18) adjCategory = 'dark'
  else if (lum > 0.85) adjCategory = 'pale'
  else if (sat > 0.70) adjCategory = 'bright'

  const nouns = NAME_NOUNS[family] || NAME_NOUNS.grayscale
  const adjs = NAME_ADJECTIVES[adjCategory] || NAME_ADJECTIVES.general

  const noun = nouns[Math.floor(Math.random() * nouns.length)]
  const adj = adjs[Math.floor(Math.random() * adjs.length)]

  if (Math.random() > 0.85) return noun
  return `${adj} ${noun}`
}

function hslToHex(h, s, l) {
  h = h % 360; if (h < 0) h += 360
  s = Math.max(0, Math.min(100, s))
  l = Math.max(0, Math.min(100, l))

  l /= 100
  const a = s * Math.min(l, 1 - l) / 100
  const f = n => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase()
}

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max)
}

function randRange(min, max) {
  return Math.random() * (max - min) + min
}

function pickRandom(array) {
  return array[Math.floor(Math.random() * array.length)]
}

function generateProceduralPalette(count = 5) {
  const moods = [
    { name: 'Vibrant Pop', sMin: 80, sMax: 100, lMin: 45, lMax: 65, contrast: 'high' },
    { name: 'Jewel Tones', sMin: 60, sMax: 85, lMin: 25, lMax: 45, contrast: 'med' },
    { name: 'Pastel Dream', sMin: 20, sMax: 60, lMin: 80, lMax: 95, contrast: 'low' },
    { name: 'Cyberpunk', sMin: 90, sMax: 100, lMin: 40, lMax: 60, contrast: 'extreme' },
    { name: 'Earthy', sMin: 20, sMax: 50, lMin: 30, lMax: 70, contrast: 'med' },
    { name: 'Muted Professional', sMin: 10, sMax: 30, lMin: 20, lMax: 85, contrast: 'high' },
    { name: 'High Key', sMin: 50, sMax: 90, lMin: 70, lMax: 90, contrast: 'low' },
    { name: 'Dark Mode', sMin: 10, sMax: 40, lMin: 5, lMax: 20, contrast: 'low' }
  ]

  const strategies = [
    { name: 'Analogous', spread: randRange(20, 50), type: 'sequence' },
    { name: 'Monochromatic', spread: 0, type: 'vary_lightness' },
    { name: 'Complementary', offset: 180, type: 'anchor' },
    { name: 'Split Complementary', offset: 150, type: 'complex' },
    { name: 'Triadic', offset: 120, type: 'anchor' },
    { name: 'Tetradic', offset: 90, type: 'anchor' },
    { name: 'Compound', spread: randRange(30, 160), type: 'sequence' },
    { name: 'Wildcard', type: 'random' }
  ]

  const baseHue = Math.floor(Math.random() * 360)
  const mood = pickRandom(moods)
  const strategy = pickRandom(strategies)

  let colorsHSL = []

  if (strategy.type === 'random') {
    for (let i = 0; i < count; i++) {
      colorsHSL.push({ h: Math.floor(Math.random() * 360), s: 0, l: 0 })
    }
  }
  else if (strategy.name === 'Monochromatic') {
    for (let i = 0; i < count; i++) colorsHSL.push({ h: baseHue, s: 0, l: 0 })
  }
  else if (strategy.name === 'Analogous' || strategy.name === 'Compound') {
    const start = baseHue - (strategy.spread / 2)
    const step = strategy.spread / count
    for (let i = 0; i < count; i++) {
      colorsHSL.push({ h: (start + (step * i)) % 360, s: 0, l: 0 })
    }
  }
  else if (strategy.type === 'anchor') {
    colorsHSL.push({ h: baseHue, s: 0, l: 0 })
    colorsHSL.push({ h: (baseHue + strategy.offset) % 360, s: 0, l: 0 })
    while (colorsHSL.length < count) {
      const parent = colorsHSL[Math.floor(Math.random() * 2)]
      const drift = randRange(-30, 30)
      colorsHSL.push({ h: (parent.h + drift) % 360, s: 0, l: 0 })
    }
  }
  else {
    const step = 360 / count
    for (let i = 0; i < count; i++) {
      colorsHSL.push({ h: (baseHue + (step * i)) % 360, s: 0, l: 0 })
    }
  }

  let lCurve = []
  const stepL = 85 / (count + 1)
  for (let i = 1; i <= count; i++) {
    if (mood.name === 'Dark Mode') lCurve.push(randRange(5, 25))
    else if (mood.name === 'Pastel Dream') lCurve.push(randRange(75, 95))
    else lCurve.push(10 + (stepL * i))
  }

  lCurve.sort(() => Math.random() - 0.5)

  colorsHSL = colorsHSL.map((c, idx) => {
    let s = randRange(mood.sMin, mood.sMax) + randRange(-5, 5)
    let l = lCurve[idx] + randRange(-5, 5)

    if (mood.name === 'Cyberpunk' && idx % 2 === 0) {
      l = randRange(5, 15)
      s = 20
    }

    return {
      h: Math.abs(c.h) % 360,
      s: clamp(s, 0, 100),
      l: clamp(l, 0, 100)
    }
  })

  if (Math.random() > 0.7 && count > 2) {
    const targetIdx = Math.floor(Math.random() * count)
    colorsHSL[targetIdx].l = colorsHSL[targetIdx].l > 50 ? randRange(10, 20) : randRange(80, 95)
    colorsHSL[targetIdx].s = randRange(85, 100)
    if (Math.random() > 0.5) {
      colorsHSL[targetIdx].h = (colorsHSL[targetIdx].h + 180) % 360
    }
  }

  return colorsHSL
}

function generateWithAI() {
  if (isSaving.value) return
  isSaving.value = true

  let limit = 5
  if (selectedTemplateId.value) {
    limit = colorsLimitForTemplate(selectedTemplateId.value)
  } else {
    limit = Math.floor(Math.random() * 4) + 3
  }

  const paletteHSL = generateProceduralPalette(limit)
  let finalHexList = paletteHSL.map(c => hslToHex(c.h, c.s, c.l))

  finalHexList = [...new Set(finalHexList)]

  while (finalHexList.length < limit) {
    const filler = hslToHex(Math.random() * 360, randRange(40, 90), randRange(10, 90))
    if (!finalHexList.includes(filler)) finalHexList.push(filler)
  }

  const slicedHex = finalHexList.slice(0, limit)

  colors.value = slicedHex.map((hex, index) => ({
    id: generateId(),
    hex: hex,
    name: generateColorName(hex),
    position: index
  }))

  setTimeout(() => isSaving.value = false, 300)
}

function getTextColor(hex, light = '#FFFFFF', dark = '#111111') {
  const h = normalizeHex(hex).replace('#', '')
  if (h.length !== 6) return light
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 150 ? dark : light
}

function roleLabelForTemplate(templateId, index) {
  if (templateId === 'quadrant') {
    return ['Primary Base', 'Primary Action', 'Primary Background', 'Secondary Base'][index] || 'Accent'
  }
  if (templateId === 'duo' || templateId === 'duo-inverted') {
    return index === 0 ? 'Primary Base' : 'Primary Accent'
  }
  if (templateId === 'triple-stripe') {
    return ['Primary Base', 'Accent', 'Support'][index] || 'Accent'
  }
  if (templateId === 'card-stack') {
    return ['Foundation', 'Support', 'Accent', 'Secondary', 'Neutral'][index] || 'Accent'
  }
  if (templateId === 'spotlight') {
    return ['Primary Base', 'Secondary Base', 'Dark Accent'][index] || 'Accent'
  }
  if (templateId === 'photo-card-stack') {
    return ['Top Note', 'Core Note', 'Anchor Note'][index] || 'Accent'
  }
  if (templateId === 'neon-grid') {
    return ['Header Base', 'Accent 1', 'Accent 2', 'Support', 'Footer'][index] || 'Accent'
  }
  if (templateId === 'masonry') {
    return ['Background', 'Primary', 'Secondary', 'Accent', 'Highlight', 'Neutral'][index] || 'Accent'
  }
  if (templateId === 'hero-label') {
    return index === 0 ? 'Hero Color' : 'Footer Accent'
  }
  if (templateId === 'split-banner') {
    return ['Banner', 'Accent', 'Support'][index] || 'Accent'
  }
  if (templateId === 'contrast-blocks') {
    return ['Dark', 'Light', 'Accent 1', 'Accent 2'][index] || 'Accent'
  }
  if (templateId === 'side-label') {
    return ['Sidebar', 'Item 1', 'Item 2', 'Item 3'][index] || 'Accent'
  }
  if (templateId === 'gradient-strip') {
    return ['Start', 'Mid', 'End'][index] || 'Stop'
  }
  if (templateId === 'tag-grid') {
    return ['Tag 1', 'Tag 2', 'Tag 3', 'Tag 4', 'Tag 5', 'Tag 6'][index] || 'Tag'
  }
  return 'Accent'
}

function onHexChange(color) {
  if (!color) return
  const previousName = color.name
  const normalized = normalizeHex(color.hex || '#000000')
  color.hex = normalized

  if (
    !previousName ||
    previousName.startsWith('Color ') ||
    previousName.startsWith('Custom Color') ||
    previousName.startsWith('Soft ') ||
    previousName.startsWith('Vibrant ') ||
    previousName.startsWith('Muted ') ||
    previousName.startsWith('Dark ') ||
    previousName.startsWith('Light ')
  ) {
    color.name = generateColorName(normalized)
  }
}


const TEMPLATE_DISPLAY_FONT = '"Roboto Condensed", "Arial Narrow", "Helvetica Neue Condensed", "Inter Tight", sans-serif'
const TEMPLATE_BODY_FONT = '"Inter", "Helvetica Neue", Arial, sans-serif'
const TEMPLATE_MONO_FONT = '"SFMono-Regular", "SF Mono", Menlo, Consolas, monospace'

function templateColor(colors, index, fallback) {
  return colors[index] || fallback
}

function templateHex(hex = '#000000') {
  return normalizeHex(hex).toUpperCase()
}

const PHOTO_BACKGROUND_ASSET_BASE = '/palette-bg'

const PHOTO_BACKGROUND_PRESETS = {
  'misty-mountain': {
    id: 'misty-mountain', label: 'Misty Mountain', category: 'cold-muted',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/misty-mountain-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(238,246,255,0.04), rgba(10,24,44,0.18))',
    fallback: 'linear-gradient(180deg, #E9F0F7 0%, #9CB0C2 48%, #33485C 100%)',
    position: 'center center'
  },
  'blueberry': {
    id: 'blueberry', label: 'Blueberry', category: 'cold-editorial',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/blueberry-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(5,15,36,0.02), rgba(7,18,36,0.24))',
    fallback: 'linear-gradient(180deg, #D9E4F2 0%, #849CB9 42%, #18263D 100%)',
    position: 'center center'
  },
  'mango-beach': {
    id: 'mango-beach', label: 'Mango Beach', category: 'tropical',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/mango-beach-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.01), rgba(249,115,22,0.08))',
    fallback: 'linear-gradient(180deg, #76C9E6 0%, #F6C89C 54%, #EF9466 100%)',
    position: 'center center'
  },
  'soft-cloud': {
    id: 'soft-cloud', label: 'Soft Cloud', category: 'pastel',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/soft-cloud-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(99,102,241,0.05))',
    fallback: 'linear-gradient(180deg, #F5F7FF 0%, #E6EBF5 50%, #CBD4E4 100%)',
    position: 'center center'
  },
  'editorial-stone': {
    id: 'editorial-stone', label: 'Editorial Stone', category: 'neutral-dark',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/editorial-stone-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(10,14,22,0.03), rgba(10,14,22,0.24))',
    fallback: 'linear-gradient(180deg, #B8BEC5 0%, #6E7883 44%, #202832 100%)',
    position: 'center center'
  },
  'desert-dune': {
    id: 'desert-dune', label: 'Desert Dune', category: 'warm-earth',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/desert-dune-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,244,224,0.00), rgba(104,55,28,0.12))',
    fallback: 'linear-gradient(180deg, #F2D2AA 0%, #D29362 48%, #985337 100%)',
    position: 'center center'
  },
  'botanical-greenhouse': {
    id: 'botanical-greenhouse', label: 'Botanical Greenhouse', category: 'botanical',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/botanical-greenhouse-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(236,253,245,0.01), rgba(4,65,48,0.14))',
    fallback: 'linear-gradient(180deg, #C4D3BF 0%, #759078 46%, #294D3A 100%)',
    position: 'center center'
  },
  'lavender-dusk': {
    id: 'lavender-dusk', label: 'Lavender Dusk', category: 'violet-dusk',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/lavender-dusk-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.00), rgba(45,25,72,0.15))',
    fallback: 'linear-gradient(180deg, #C1AED6 0%, #9B7DB7 45%, #574467 100%)',
    position: 'center center'
  },
  'arctic-ice': {
    id: 'arctic-ice', label: 'Arctic Ice', category: 'icy-light',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/arctic-ice-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(14,116,144,0.08))',
    fallback: 'linear-gradient(180deg, #EEF9FC 0%, #C4E4EF 48%, #75AFC4 100%)',
    position: 'center center'
  },
  'blush-floral': {
    id: 'blush-floral', label: 'Blush Floral', category: 'soft-warm',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/blush-floral-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(190,92,112,0.06))',
    fallback: 'linear-gradient(180deg, #F8E8E4 0%, #E8C4BE 50%, #CB9396 100%)',
    position: 'center center'
  },
  'autumn-forest': {
    id: 'autumn-forest', label: 'Autumn Forest', category: 'warm-rich',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/autumn-forest-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,199,92,0.00), rgba(91,39,12,0.14))',
    fallback: 'linear-gradient(180deg, #E9A449 0%, #BA5E28 50%, #5E2E1B 100%)',
    position: 'center center'
  },
  'lavender-fields': {
    id: 'lavender-fields', label: 'Lavender Fields', category: 'violet-light',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/lavender-fields-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,224,211,0.01), rgba(71,39,104,0.09))',
    fallback: 'linear-gradient(180deg, #E8B5C9 0%, #A276C2 48%, #67507F 100%)',
    position: 'center center'
  },
  'alpine-lake': {
    id: 'alpine-lake', label: 'Alpine Lake', category: 'green-cool',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/alpine-lake-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(231,250,246,0.00), rgba(9,69,67,0.12))',
    fallback: 'linear-gradient(180deg, #BDD8D4 0%, #4F9A96 48%, #1C5147 100%)',
    position: 'center center'
  },
  'mediterranean-courtyard': {
    id: 'mediterranean-courtyard', label: 'Mediterranean', category: 'terracotta-green',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/mediterranean-courtyard-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,238,210,0.00), rgba(116,55,26,0.08))',
    fallback: 'linear-gradient(180deg, #EAB080 0%, #C97854 50%, #74543C 100%)',
    position: 'center center'
  },
  'snowy-valley': {
    id: 'snowy-valley', label: 'Snowy Valley', category: 'snow-neutral',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/snowy-valley-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(44,67,92,0.10))',
    fallback: 'linear-gradient(180deg, #E8EFF6 0%, #B9C9D9 48%, #65788B 100%)',
    position: 'center center'
  },
  'neon-city': {
    id: 'neon-city', label: 'Neon City', category: 'neon-dark',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/neon-city-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(8,8,24,0.02), rgba(7,6,28,0.18))',
    fallback: 'linear-gradient(180deg, #241B4E 0%, #552A75 42%, #11152B 100%)',
    position: 'center center'
  },
  'spa-retreat': {
    id: 'spa-retreat', label: 'Spa Retreat', category: 'warm-neutral-green',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/spa-retreat-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.01), rgba(91,79,55,0.05))',
    fallback: 'linear-gradient(180deg, #EFE7D9 0%, #D8CBB8 50%, #9A9A7B 100%)',
    position: 'center center'
  },
  'golden-wheat': {
    id: 'golden-wheat', label: 'Golden Wheat', category: 'golden',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/golden-wheat-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,231,150,0.00), rgba(120,67,8,0.10))',
    fallback: 'linear-gradient(180deg, #FFD77C 0%, #D89B3E 48%, #875421 100%)',
    position: 'center center'
  },
  'minimal-atrium': {
    id: 'minimal-atrium', label: 'Minimal Atrium', category: 'warm-minimal',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/minimal-atrium-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,255,0.01), rgba(91,80,62,0.04))',
    fallback: 'linear-gradient(180deg, #EEE8DD 0%, #D6CDBC 52%, #A49B8C 100%)',
    position: 'center center'
  },
  'sakura-garden': {
    id: 'sakura-garden', label: 'Sakura Garden', category: 'pink-spring',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/sakura-garden-01.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,246,249,0.01), rgba(179,91,113,0.05))',
    fallback: 'linear-gradient(180deg, #F8DCE5 0%, #EDB8CA 48%, #A98C9C 100%)',
    position: 'center center'
  },
  'red-berries': {
    id: 'red-berries', label: 'Red Berries', category: 'fruit-red',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/red-berries.jpg`],
    overlay: 'linear-gradient(180deg, rgba(80,0,12,0.01), rgba(72,8,20,0.12))',
    fallback: 'linear-gradient(180deg, #D7444F 0%, #A91F35 52%, #5F1020 100%)',
    position: 'center center'
  },
  'cherries': {
    id: 'cherries', label: 'Cherries', category: 'fruit-burgundy',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/cherries.jpg`],
    overlay: 'linear-gradient(180deg, rgba(40,0,8,0.02), rgba(35,3,12,0.16))',
    fallback: 'linear-gradient(180deg, #7D1F2B 0%, #4E101B 54%, #25070D 100%)',
    position: 'center center'
  },
  'oranges': {
    id: 'oranges', label: 'Oranges', category: 'fruit-orange',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/oranges.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,244,220,0.01), rgba(173,74,0,0.08))',
    fallback: 'linear-gradient(180deg, #FFB443 0%, #F47D1F 52%, #B9450C 100%)',
    position: 'center center'
  },
  'mangoes': {
    id: 'mangoes', label: 'Mangoes', category: 'fruit-golden',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/mangoes.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,246,209,0.00), rgba(196,88,12,0.08))',
    fallback: 'linear-gradient(180deg, #FFD44A 0%, #F5A623 50%, #D75A1E 100%)',
    position: 'center center'
  },
  'lemons': {
    id: 'lemons', label: 'Lemons', category: 'fruit-yellow',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/lemons.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,255,225,0.01), rgba(180,142,0,0.05))',
    fallback: 'linear-gradient(180deg, #FFF39A 0%, #F4D93F 52%, #BFAE22 100%)',
    position: 'center center'
  },
  'green-apples': {
    id: 'green-apples', label: 'Green Apples', category: 'fruit-green-soft',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/green-apples.jpg`],
    overlay: 'linear-gradient(180deg, rgba(244,255,225,0.01), rgba(55,98,14,0.08))',
    fallback: 'linear-gradient(180deg, #DDEB8D 0%, #9FBD42 50%, #527423 100%)',
    position: 'center center'
  },
  'kiwi-lime': {
    id: 'kiwi-lime', label: 'Kiwi Lime', category: 'fruit-green-vivid',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/kiwi-lime.jpg`],
    overlay: 'linear-gradient(180deg, rgba(244,255,221,0.00), rgba(42,105,10,0.08))',
    fallback: 'linear-gradient(180deg, #C7EF71 0%, #78B82A 48%, #2F7118 100%)',
    position: 'center center'
  },
  'watermelon': {
    id: 'watermelon', label: 'Watermelon', category: 'fruit-coral-green',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/watermelon.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,239,234,0.00), rgba(129,24,38,0.07))',
    fallback: 'linear-gradient(180deg, #F78C82 0%, #E94D59 50%, #557B35 100%)',
    position: 'center center'
  },
  'papaya': {
    id: 'papaya', label: 'Papaya', category: 'fruit-tropical-orange',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/papaya.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,237,208,0.00), rgba(155,58,14,0.08))',
    fallback: 'linear-gradient(180deg, #FFA85A 0%, #F06B35 50%, #486D2E 100%)',
    position: 'center center'
  },
  'mixed-fruit': {
    id: 'mixed-fruit', label: 'Mixed Fruit', category: 'fruit-mixed',
    images: [`${PHOTO_BACKGROUND_ASSET_BASE}/mixed-fruit.jpg`],
    overlay: 'linear-gradient(180deg, rgba(255,248,229,0.00), rgba(100,74,31,0.05))',
    fallback: 'linear-gradient(180deg, #F6CB63 0%, #E9853F 44%, #8EAA54 100%)',
    position: 'center center'
  }
}

function hexToRgb(hex) {
  const value = normalizeHex(hex).replace('#', '')
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  }
}

function rgbToHsl(r, g, b) {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  let h = 0
  let s = 0
  const l = (max + min) / 2
  const d = max - min

  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60; break
      case gn: h = ((bn - rn) / d + 2) * 60; break
      default: h = ((rn - gn) / d + 4) * 60; break
    }
  }

  return { h, s, l }
}

function colorFamilyFromHue(hue, sat) {
  if (sat < 0.08) return 'neutral'
  if (hue < 15 || hue >= 345) return 'red'
  if (hue < 40) return 'orange'
  if (hue < 70) return 'yellow'
  if (hue < 160) return 'green'
  if (hue < 205) return 'teal'
  if (hue < 265) return 'blue'
  if (hue < 320) return 'purple'
  return 'pink'
}

function hueDistance(a, b) {
  const diff = Math.abs(a - b) % 360
  return Math.min(diff, 360 - diff)
}

const PHOTO_MATCH_PROFILES = {
  'misty-mountain': { hue: 215, sat: 0.22, light: 0.58, contrast: 0.42, families: { blue: 2.4, neutral: 1.6, teal: 0.5 } },
  'blueberry': { hue: 225, sat: 0.38, light: 0.40, contrast: 0.46, families: { blue: 2.8, purple: 1.0, neutral: 0.5 } },
  'mango-beach': { hue: 32, sat: 0.60, light: 0.64, contrast: 0.42, families: { orange: 2.1, yellow: 1.7, teal: 1.6, blue: 1.2, pink: 0.5 } },
  'soft-cloud': { hue: 220, sat: 0.14, light: 0.83, contrast: 0.22, families: { neutral: 2.6, blue: 0.8, purple: 0.4 } },
  'editorial-stone': { hue: 215, sat: 0.10, light: 0.34, contrast: 0.48, families: { neutral: 3.2, blue: 0.4 } },
  'desert-dune': { hue: 28, sat: 0.44, light: 0.48, contrast: 0.38, families: { orange: 2.6, red: 1.2, yellow: 0.6, neutral: 0.5 } },
  'botanical-greenhouse': { hue: 130, sat: 0.42, light: 0.42, contrast: 0.40, families: { green: 3.2, teal: 0.7, neutral: 0.3 } },
  'lavender-dusk': { hue: 282, sat: 0.42, light: 0.48, contrast: 0.40, families: { purple: 2.8, pink: 1.1, blue: 0.3 } },
  'arctic-ice': { hue: 195, sat: 0.30, light: 0.72, contrast: 0.30, families: { teal: 2.4, blue: 2.0, neutral: 0.7 } },
  'blush-floral': { hue: 344, sat: 0.34, light: 0.72, contrast: 0.28, families: { pink: 2.7, red: 0.8, neutral: 0.8 } },
  'autumn-forest': { hue: 26, sat: 0.62, light: 0.42, contrast: 0.46, families: { orange: 2.5, red: 1.8, yellow: 1.0, green: 0.25 } },
  'lavender-fields': { hue: 286, sat: 0.52, light: 0.65, contrast: 0.30, families: { purple: 2.7, pink: 1.3, yellow: 0.25 } },
  'alpine-lake': { hue: 174, sat: 0.40, light: 0.48, contrast: 0.42, families: { teal: 2.4, green: 2.0, blue: 1.0, neutral: 0.2 } },
  'mediterranean-courtyard': { hue: 24, sat: 0.48, light: 0.60, contrast: 0.36, families: { orange: 2.0, red: 0.9, green: 1.4, pink: 0.35, neutral: 0.35 } },
  'snowy-valley': { hue: 212, sat: 0.16, light: 0.68, contrast: 0.34, families: { neutral: 2.2, blue: 1.5, teal: 0.25 } },
  'neon-city': { hue: 292, sat: 0.76, light: 0.34, contrast: 0.55, families: { purple: 2.2, pink: 2.0, blue: 1.6, teal: 1.0 } },
  'spa-retreat': { hue: 95, sat: 0.20, light: 0.72, contrast: 0.28, families: { neutral: 2.0, green: 1.8, yellow: 0.35 } },
  'golden-wheat': { hue: 48, sat: 0.62, light: 0.62, contrast: 0.34, families: { yellow: 2.8, orange: 1.8, neutral: 0.2 } },
  'minimal-atrium': { hue: 42, sat: 0.12, light: 0.72, contrast: 0.22, families: { neutral: 3.0, yellow: 0.4, orange: 0.25 } },
  'sakura-garden': { hue: 340, sat: 0.46, light: 0.72, contrast: 0.30, families: { pink: 3.0, red: 0.5, green: 0.35, neutral: 0.3 } },
  'red-berries': { hue: 355, sat: 0.68, light: 0.48, contrast: 0.36, families: { red: 3.1, pink: 1.1, green: 0.2 } },
  'cherries': { hue: 350, sat: 0.60, light: 0.30, contrast: 0.40, families: { red: 3.3, pink: 0.5, neutral: 0.2 } },
  'oranges': { hue: 28, sat: 0.74, light: 0.62, contrast: 0.30, families: { orange: 3.3, yellow: 1.0, green: 0.2 } },
  'mangoes': { hue: 42, sat: 0.76, light: 0.66, contrast: 0.32, families: { yellow: 2.6, orange: 2.3, red: 0.4, green: 0.25 } },
  'lemons': { hue: 56, sat: 0.70, light: 0.76, contrast: 0.26, families: { yellow: 3.5, green: 0.7, neutral: 0.2 } },
  'green-apples': { hue: 92, sat: 0.50, light: 0.64, contrast: 0.30, families: { green: 3.2, yellow: 0.9, neutral: 0.2 } },
  'kiwi-lime': { hue: 108, sat: 0.72, light: 0.58, contrast: 0.34, families: { green: 3.5, yellow: 0.6, teal: 0.3 } },
  'watermelon': { hue: 352, sat: 0.72, light: 0.60, contrast: 0.36, families: { red: 2.5, pink: 1.8, green: 1.3 } },
  'papaya': { hue: 22, sat: 0.72, light: 0.58, contrast: 0.38, families: { orange: 2.8, red: 1.3, yellow: 0.8, green: 0.8 } },
  'mixed-fruit': { hue: 38, sat: 0.62, light: 0.62, contrast: 0.40, families: { orange: 1.7, yellow: 1.5, red: 1.4, green: 1.3, pink: 0.4 } }
}

function analyzePaletteMood(palette) {
  const summary = {
    families: { red: 0, orange: 0, yellow: 0, green: 0, teal: 0, blue: 0, purple: 0, pink: 0, neutral: 0 },
    avgSat: 0,
    avgLight: 0,
    minLight: 1,
    maxLight: 0,
    contrast: 0,
    dominantHue: 0,
    vividRatio: 0,
    pastelRatio: 0,
    darkRatio: 0,
    neutralRatio: 0
  }

  if (!Array.isArray(palette) || !palette.length) {
    return { presetId: 'soft-cloud', scores: {}, ...summary }
  }

  let satTotal = 0
  let lightTotal = 0
  let hueX = 0
  let hueY = 0
  let hueWeightTotal = 0
  let vividCount = 0
  let pastelCount = 0
  let darkCount = 0

  palette.forEach(color => {
    const { r, g, b } = hexToRgb(color.hex || '#000000')
    const { h, s, l } = rgbToHsl(r, g, b)
    const family = colorFamilyFromHue(h, s)
    summary.families[family] = (summary.families[family] || 0) + 1
    satTotal += s
    lightTotal += l
    summary.minLight = Math.min(summary.minLight, l)
    summary.maxLight = Math.max(summary.maxLight, l)

    const hueWeight = Math.max(0.04, s) * (0.7 + Math.abs(l - 0.5))
    const angle = h * Math.PI / 180
    hueX += Math.cos(angle) * hueWeight
    hueY += Math.sin(angle) * hueWeight
    hueWeightTotal += hueWeight

    if (s >= 0.62) vividCount += 1
    if (l >= 0.72 && s <= 0.48) pastelCount += 1
    if (l <= 0.30) darkCount += 1
  })

  summary.avgSat = satTotal / palette.length
  summary.avgLight = lightTotal / palette.length
  summary.contrast = summary.maxLight - summary.minLight
  summary.vividRatio = vividCount / palette.length
  summary.pastelRatio = pastelCount / palette.length
  summary.darkRatio = darkCount / palette.length
  summary.neutralRatio = summary.families.neutral / palette.length

  if (hueWeightTotal > 0) {
    const angle = Math.atan2(hueY, hueX) * 180 / Math.PI
    summary.dominantHue = (angle + 360) % 360
  }

  const f = summary.families
  const coolCount = f.blue + f.teal + f.purple
  const warmCount = f.red + f.orange + f.yellow + f.pink
  const warmEarthCount = f.red + f.orange + f.yellow
  const purplePinkCount = f.purple + f.pink
  const blueTealCount = f.blue + f.teal
  const vividCoolCount = f.purple + f.pink + f.blue + f.teal
  const chromaFamilyCount = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink']
    .filter(family => f[family] > 0).length

  const scores = {}

  Object.entries(PHOTO_MATCH_PROFILES).forEach(([id, profile]) => {
    let score = 0

    Object.entries(profile.families).forEach(([family, weight]) => {
      score += ((f[family] || 0) / palette.length) * weight * 3.0
    })

    if (summary.avgSat > 0.08) {
      const hScore = 1 - hueDistance(summary.dominantHue, profile.hue) / 180
      score += hScore * 1.75
    }

    score += (1 - Math.min(1, Math.abs(summary.avgSat - profile.sat))) * 1.45
    score += (1 - Math.min(1, Math.abs(summary.avgLight - profile.light))) * 1.60
    score += (1 - Math.min(1, Math.abs(summary.contrast - profile.contrast))) * 0.85

    scores[id] = score
  })

  const bonus = (id, value) => { scores[id] = (scores[id] || 0) + value }
  const penalty = (id, value) => { scores[id] = (scores[id] || 0) - value }

  if (f.green >= 2) bonus('botanical-greenhouse', 4.4)
  if (f.green >= 1 && blueTealCount >= 1) bonus('alpine-lake', 4.2)
  if (f.green >= 1 && warmEarthCount >= 1 && warmCount > coolCount) bonus('mediterranean-courtyard', 3.8)
  if (f.green >= 1 && summary.neutralRatio >= 0.33 && summary.avgLight >= 0.60) bonus('spa-retreat', 4.2)

  if (f.purple >= 2 && summary.avgLight >= 0.56) bonus('lavender-fields', 4.4)
  if (purplePinkCount >= 2 && summary.avgLight < 0.58) bonus('lavender-dusk', 4.1)
  if (f.pink >= 2 && summary.avgLight >= 0.58) bonus('sakura-garden', 4.4)
  if (f.pink >= 1 && summary.pastelRatio >= 0.66) bonus('blush-floral', 3.6)

  if (blueTealCount >= 2 && summary.avgLight >= 0.66) bonus('arctic-ice', 4.1)
  if (f.blue >= 1 && summary.neutralRatio >= 0.33 && summary.avgSat <= 0.30 && summary.avgLight >= 0.56) bonus('snowy-valley', 4.0)
  if (f.blue >= 2 && summary.avgLight <= 0.58) bonus('blueberry', 4.2)
  if (f.blue >= 1 && summary.neutralRatio >= 0.33 && summary.avgSat <= 0.36) bonus('misty-mountain', 3.4)

  if (warmEarthCount >= 2 && summary.avgLight <= 0.54) bonus('autumn-forest', 4.2)
  if (f.yellow >= 1 && (f.yellow + f.orange) >= 2 && summary.avgLight >= 0.52) bonus('golden-wheat', 4.2)
  if ((f.orange + f.red) >= 2 && summary.avgLight >= 0.40 && summary.avgLight <= 0.66) bonus('desert-dune', 3.3)

  if ((f.orange + f.yellow) >= 1 && blueTealCount >= 1 && summary.avgLight >= 0.48) bonus('mango-beach', 4.6)

  if (f.red >= 2) {
    if (summary.avgLight <= 0.42 || summary.darkRatio >= 0.34) bonus('cherries', 5.6)
    else bonus('red-berries', 5.2)
  }
  if ((f.red + f.pink) >= 2 && f.green >= 1 && summary.avgLight >= 0.46) bonus('watermelon', 5.7)
  if (f.orange >= 2 && f.yellow === 0) bonus('oranges', 5.4)
  if (f.yellow >= 2 && summary.avgLight >= 0.66) bonus('lemons', 5.6)
  if (f.yellow >= 1 && f.orange >= 1 && summary.avgLight >= 0.56) bonus('mangoes', 5.2)
  if (f.green >= 2) {
    if (summary.avgSat >= 0.56) bonus('kiwi-lime', 5.6)
    else if (summary.avgLight >= 0.52) bonus('green-apples', 5.3)
  }
  if (f.orange >= 1 && f.red >= 1 && f.green >= 1) bonus('papaya', 5.5)
  if (chromaFamilyCount >= 3 && warmEarthCount >= 2 && (f.green >= 1 || f.pink >= 1)) bonus('mixed-fruit', 5.3)

  if (summary.vividRatio >= 0.66 && vividCoolCount >= 2 && summary.avgLight <= 0.52) bonus('neon-city', 5.0)
  if (summary.neutralRatio >= 0.66 && summary.avgLight >= 0.62) bonus('minimal-atrium', 4.6)
  if (summary.neutralRatio >= 0.66 && summary.avgLight <= 0.48) bonus('editorial-stone', 4.8)
  if (summary.pastelRatio >= 0.66 && summary.avgSat <= 0.30 && coolCount >= warmCount) bonus('soft-cloud', 4.3)

  if (warmCount >= 2) {
    penalty('misty-mountain', 1.8)
    penalty('snowy-valley', 1.5)
    penalty('arctic-ice', 1.3)
  }
  if (coolCount >= 2) {
    penalty('desert-dune', 1.4)
    penalty('autumn-forest', 1.4)
    penalty('golden-wheat', 1.1)
  }
  if (summary.avgLight >= 0.72) {
    penalty('editorial-stone', 1.8)
    penalty('neon-city', 1.5)
  }
  if (summary.avgLight <= 0.34) {
    penalty('soft-cloud', 1.6)
    penalty('minimal-atrium', 1.4)
    penalty('blush-floral', 1.1)
  }
  if (summary.avgSat <= 0.18) penalty('neon-city', 2.2)
  if (f.green === 0) {
    penalty('botanical-greenhouse', 0.8)
    penalty('spa-retreat', 0.45)
  }
  if (f.pink === 0 && f.purple === 0) {
    penalty('sakura-garden', 0.8)
    penalty('lavender-fields', 0.6)
  }
  if ((f.red + f.pink) === 0) {
    penalty('red-berries', 3.4)
    penalty('cherries', 3.6)
    penalty('watermelon', 3.0)
  }
  if (f.orange === 0) {
    penalty('oranges', 3.5)
    penalty('papaya', 2.8)
  }
  if (f.yellow === 0) {
    penalty('lemons', 3.6)
    penalty('mangoes', 2.6)
  }
  if (f.green === 0) {
    penalty('green-apples', 3.6)
    penalty('kiwi-lime', 3.6)
    penalty('papaya', 1.4)
    penalty('watermelon', 1.2)
  }
  if (chromaFamilyCount < 3) penalty('mixed-fruit', 3.2)
  if (coolCount >= 2 && warmCount === 0) {
    penalty('red-berries', 2.2)
    penalty('cherries', 2.2)
    penalty('oranges', 2.2)
    penalty('mangoes', 2.2)
    penalty('lemons', 2.0)
    penalty('papaya', 2.2)
    penalty('mixed-fruit', 1.8)
  }

  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1])
  const presetId = ranked[0]?.[0] || 'soft-cloud'

  return {
    presetId,
    scores,
    ...summary
  }
}

function hashPalette(colors = []) {
  return colors
    .map(c => normalizeHex(c.hex || '#000000').replace('#', ''))
    .join('')
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)
}

function getPhotoBackgroundPreset(colors = []) {
  const analysis = analyzePaletteMood(colors)
  const preset = PHOTO_BACKGROUND_PRESETS[analysis.presetId] || PHOTO_BACKGROUND_PRESETS['soft-cloud']
  const images = Array.isArray(preset.images) ? preset.images : []
  const selectedImage = images.length ? images[hashPalette(colors) % images.length] : ''

  const layers = [
    preset.overlay,
    selectedImage ? `url("${selectedImage}")` : '',
    preset.fallback
  ].filter(Boolean)

  return {
    ...preset,
    image: selectedImage,
    cssBackground: layers.join(', '),
    layerCount: layers.length,
    matchScore: analysis.scores?.[analysis.presetId] ?? 0,
    analysis
  }
}

async function createPhotoStackExportBackground(colors = []) {
  const background = getPhotoBackgroundPreset(colors)
  if (!background?.image) return ''

  const width = 2160
  const height = 2700
  const overscan = 1.045
  const blurPx = 16

  const img = await new Promise(resolve => {
    const el = new Image()
    el.crossOrigin = 'anonymous'
    el.onload = () => resolve(el)
    el.onerror = () => resolve(null)
    el.src = background.image
  })

  if (!img || !img.naturalWidth || !img.naturalHeight) return ''

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.save()
  ctx.filter = `blur(${blurPx}px)`

  const baseScale = Math.max(width / img.naturalWidth, height / img.naturalHeight)
  const scale = baseScale * overscan
  const drawWidth = img.naturalWidth * scale
  const drawHeight = img.naturalHeight * scale
  const dx = (width - drawWidth) / 2
  const dy = (height - drawHeight) / 2

  ctx.drawImage(img, dx, dy, drawWidth, drawHeight)
  ctx.restore()

  return canvas.toDataURL('image/png')
}

const EXPORT_CARD_SHADOW_CACHE = new Map()

function createExportCardShadowDataUrl(width = 468, height = 308, radius = 54) {
  const key = `${width}x${height}r${radius}`
  if (EXPORT_CARD_SHADOW_CACHE.has(key)) return EXPORT_CARD_SHADOW_CACHE.get(key)

  const scale = 2
  const padX = 105
  const padTop = 70
  const padBottom = 125
  const canvas = document.createElement('canvas')
  canvas.width = (width + padX * 2) * scale
  canvas.height = (height + padTop + padBottom) * scale
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  ctx.scale(scale, scale)

  const drawRoundRect = () => {
    const x = padX
    const y = padTop
    const w = width
    const h = height
    const r = radius
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.quadraticCurveTo(x + w, y, x + w, y + r)
    ctx.lineTo(x + w, y + h - r)
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
    ctx.lineTo(x + r, y + h)
    ctx.quadraticCurveTo(x, y + h, x, y + h - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
    ctx.closePath()
  }

  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.24)'
  ctx.shadowBlur = 60
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 34
  ctx.fillStyle = 'rgba(0,0,0,0.65)'
  drawRoundRect()
  ctx.fill()
  ctx.restore()

  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.12)'
  ctx.shadowBlur = 24
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 11
  ctx.fillStyle = 'rgba(0,0,0,0.45)'
  drawRoundRect()
  ctx.fill()
  ctx.restore()

  ctx.save()
  ctx.globalCompositeOperation = 'destination-out'
  drawRoundRect()
  ctx.fillStyle = '#000'
  ctx.fill()
  ctx.restore()

  const dataUrl = canvas.toDataURL('image/png')
  EXPORT_CARD_SHADOW_CACHE.set(key, dataUrl)
  return dataUrl
}

function templateBrand(color, isExport, position = 'right') {
  const alignClass = position === 'center'
    ? 'left-1/2 -translate-x-1/2'
    : position === 'left'
      ? 'left-8'
      : 'right-8'

  return h(
    'div',
    {
      class: `absolute bottom-6 ${alignClass} z-20 font-black uppercase`,
      style: {
        color,
        fontFamily: TEMPLATE_BODY_FONT,
        fontSize: isExport ? '18px' : '8px',
        letterSpacing: isExport ? '0.08em' : '0.12em'
      }
    },
  )
}

function templateRoleClass(isExport) {
  return isExport
    ? 'text-[18px] uppercase tracking-[0.18em] font-semibold'
    : 'text-[8px] uppercase tracking-[0.16em] font-semibold'
}

function templateHexClass(isExport) {
  return isExport
    ? 'text-[24px] tracking-[0.06em]'
    : 'text-[10px] tracking-[0.04em]'
}

const DuoSplitTemplate = defineComponent({
  name: 'DuoSplitTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const top = templateColor(props.colors, 0, { hex: '#C21E56', name: 'Raspberry', roleLabel: 'Primary Base' })
      const bottom = templateColor(props.colors, 1, { hex: '#3B1F4B', name: 'Blackberry', roleLabel: 'Primary Accent' })

      const renderBlock = (c, align = 'left') => {
        const textColor = getTextColor(c.hex)
        return h(
          'div',
          {
            class: `relative flex-1 flex ${align === 'right' ? 'items-end text-right' : 'items-start'} justify-between flex-col`,
            style: {
              backgroundColor: c.hex,
              color: textColor,
              padding: isExport ? '72px 76px 64px' : '22px 24px 20px',
              fontFamily: TEMPLATE_BODY_FONT
            }
          },
          [
            h('p', { class: templateRoleClass(isExport) }, c.roleLabel || 'Primary Base'),
            h('div', { class: align === 'right' ? 'ml-auto max-w-[78%]' : 'max-w-[78%]' }, [
              h('p', {
                class: isExport
                  ? 'text-[86px] leading-[0.88] font-black tracking-[-0.045em]'
                  : 'text-[28px] leading-[0.9] font-black tracking-[-0.04em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase()),
              h('p', {
                class: `${templateHexClass(isExport)} mt-5`,
                style: { fontFamily: TEMPLATE_MONO_FONT }
              }, `HEX ${templateHex(c.hex).replace('#', '')}`)
            ])
          ]
        )
      }

      return h('div', { class: 'relative h-full w-full flex flex-col overflow-hidden' }, [
        renderBlock(top, 'left'),
        renderBlock(bottom, 'right'),
        templateBrand(getTextColor(bottom.hex), isExport, 'right')
      ])
    }
  }
})

const NeonGridTemplate = defineComponent({
  name: 'NeonGridTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const base = templateColor(props.colors, 0, { hex: '#111111', name: 'Noir', roleLabel: 'Header Base' })
      const accents = [
        templateColor(props.colors, 1, { hex: '#C4FF4D', name: 'Lime', roleLabel: 'Accent 1' }),
        templateColor(props.colors, 2, { hex: '#BA8CFF', name: 'Violet', roleLabel: 'Accent 2' }),
        templateColor(props.colors, 3, { hex: '#32D7FF', name: 'Cyan', roleLabel: 'Support' }),
        templateColor(props.colors, 4, { hex: '#F2F2F2', name: 'Cloud', roleLabel: 'Neutral' })
      ]
      const baseText = getTextColor(base.hex)

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: { backgroundColor: base.hex, fontFamily: TEMPLATE_BODY_FONT }
      }, [
        h('div', {
          class: 'absolute inset-x-0 top-0',
          style: {
            height: isExport ? '42%' : '40%',
            padding: isExport ? '74px 72px 54px' : '22px 22px 18px',
            color: baseText
          }
        }, [
          h('p', { class: templateRoleClass(isExport) }, 'EDITORIAL PALETTE / 05'),
          h('p', {
            class: isExport
              ? 'mt-5 max-w-[75%] text-[72px] leading-[0.9] font-black tracking-[-0.05em]'
              : 'mt-2 max-w-[82%] text-[24px] leading-[0.92] font-black tracking-[-0.04em]',
            style: { fontFamily: TEMPLATE_DISPLAY_FONT }
          }, (base.name || 'Night Study').toUpperCase()),
          h('p', {
            class: isExport ? 'mt-6 text-[21px]' : 'mt-2 text-[9px]',
            style: { fontFamily: TEMPLATE_MONO_FONT }
          }, templateHex(base.hex))
        ]),
        h('div', {
          class: 'absolute inset-x-0 bottom-0 grid grid-cols-2',
          style: { height: isExport ? '58%' : '60%' }
        }, accents.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'relative flex flex-col justify-end',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '34px 34px 30px' : '12px'
            }
          }, [
            h('p', {
              class: isExport
                ? 'text-[12px] uppercase tracking-[0.18em] font-semibold'
                : 'text-[7px] uppercase tracking-[0.12em] font-semibold'
            }, c.roleLabel || `Accent ${index + 1}`),
            h('p', {
              class: isExport
                ? 'mt-2 text-[30px] leading-none font-black tracking-[-0.03em]'
                : 'mt-1 text-[11px] leading-none font-bold tracking-[-0.02em]',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (c.name || 'Color').toUpperCase()),
            h('p', {
              class: isExport ? 'mt-3 text-[15px]' : 'mt-1 text-[8px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        })),
        templateBrand(baseText, isExport, 'right')
      ])
    }
  }
})

const TripleStripeTemplate = defineComponent({
  name: 'TripleStripeTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const items = [
        templateColor(props.colors, 0, { hex: '#2E3A59', name: 'Blueberry Deep', roleLabel: 'Primary Base' }),
        templateColor(props.colors, 1, { hex: '#C8C1D9', name: 'Frosted Lilac', roleLabel: 'Support' }),
        templateColor(props.colors, 2, { hex: '#7C8DA6', name: 'Soft Denim', roleLabel: 'Accent' })
      ]

      return h('div', {
        class: 'relative h-full w-full flex flex-col overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        ...items.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'relative flex-1 flex items-center justify-between',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '34px 62px' : '12px 18px'
            }
          }, [
            h('div', [
              h('p', {
                class: isExport
                  ? 'text-[12px] uppercase tracking-[0.18em] font-semibold opacity-80'
                  : 'text-[7px] uppercase tracking-[0.12em] font-semibold opacity-80'
              }, c.roleLabel || `Tone ${index + 1}`),
              h('p', {
                class: isExport
                  ? 'mt-2 text-[50px] leading-none font-black tracking-[-0.04em]'
                  : 'mt-1 text-[17px] leading-none font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase())
            ]),
            h('div', { class: 'text-right' }, [
              h('p', {
                class: isExport ? 'text-[13px] uppercase tracking-[0.14em]' : 'text-[7px] uppercase tracking-[0.12em]'
              }, 'HEX'),
              h('p', {
                class: isExport ? 'mt-2 text-[23px]' : 'mt-1 text-[9px]',
                style: { fontFamily: TEMPLATE_MONO_FONT }
              }, templateHex(c.hex).replace('#', ''))
            ])
          ])
        }),
        templateBrand(getTextColor(items[2].hex), isExport, 'right')
      ])
    }
  }
})

const CardStackTemplate = defineComponent({
  name: 'CardStackTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const cards = Array.from({ length: 5 }).map((_, i) =>
        templateColor(props.colors, i, {
          hex: ['#0F172A', '#1E293B', '#38BDF8', '#A78BFA', '#F8FAFC'][i],
          name: ['Midnight', 'Slate', 'Sky', 'Violet', 'Cloud'][i],
          roleLabel: ['Foundation', 'Support', 'Accent', 'Secondary', 'Neutral'][i]
        })
      )

      const accent = cards[2].hex

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: {
          fontFamily: TEMPLATE_BODY_FONT,
          background: `
            radial-gradient(circle at 18% 88%, ${accent}55 0, transparent 42%),
            radial-gradient(circle at 86% 18%, ${cards[3].hex}44 0, transparent 40%),
            linear-gradient(145deg, #050505 0%, #141414 46%, #090909 100%)
          `
        }
      }, [
        h('div', {
          class: 'absolute inset-0 opacity-40',
          style: {
            backgroundImage: 'linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)',
            backgroundSize: isExport ? '54px 54px' : '18px 18px'
          }
        }),
        h('div', {
          class: 'relative z-10 h-full w-full flex flex-col items-center justify-center',
          style: {
            gap: isExport ? '18px' : '6px',
            padding: isExport ? '92px 120px 82px' : '30px 36px 26px'
          }
        }, cards.map((c, index) => {
          const color = getTextColor(c.hex)
          const offsets = isExport
            ? ['-36px', '-18px', '0px', '18px', '36px']
            : ['-12px', '-6px', '0px', '6px', '12px']

          return h('div', {
            class: 'w-full flex items-center justify-between',
            style: {
              minHeight: isExport ? '128px' : '43px',
              backgroundColor: c.hex,
              color,
              borderRadius: isExport ? '34px' : '13px',
              padding: isExport ? '0 34px' : '0 12px',
              transform: `translateX(${offsets[index]})`,
              boxShadow: isExport
                ? 'none'
                : '0 10px 20px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.12)'
            }
          }, [
            h('div', [
              h('p', {
                class: isExport
                  ? 'text-[11px] uppercase tracking-[0.16em] font-semibold opacity-75'
                  : 'text-[6px] uppercase tracking-[0.10em] font-semibold opacity-75'
              }, c.roleLabel || `Tone ${index + 1}`),
              h('p', {
                class: isExport
                  ? 'mt-1 text-[28px] leading-none font-black tracking-[-0.04em]'
                  : 'mt-0.5 text-[10px] leading-none font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase())
            ]),
            h('p', {
              class: isExport ? 'text-[16px]' : 'text-[7px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        })),
        templateBrand('#FFFFFF', isExport, 'center')
      ])
    }
  }
})

const QuadrantTemplate = defineComponent({
  name: 'QuadrantTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const blocks = [
        templateColor(props.colors, 0, { hex: '#C21E56', name: 'Raspberry', roleLabel: 'Primary Base' }),
        templateColor(props.colors, 1, { hex: '#3B1F4B', name: 'Blackberry', roleLabel: 'Primary Base' }),
        templateColor(props.colors, 2, { hex: '#F4EDE2', name: 'Oat Milk', roleLabel: 'Neutral Base' }),
        templateColor(props.colors, 3, { hex: '#6EA857', name: 'Fresh Basil', roleLabel: 'Secondary Base' })
      ]

      return h('div', {
        class: 'relative h-full w-full grid grid-cols-2 grid-rows-2 overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        ...blocks.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'relative flex flex-col justify-between',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '66px 60px 58px' : '20px 18px 18px'
            }
          }, [
            h('p', {
              class: isExport
                ? 'text-[18px] font-semibold tracking-[-0.01em]'
                : 'text-[8px] font-semibold'
            }, c.roleLabel || 'Primary Base'),
            h('div', [
              h('p', {
                class: isExport
                  ? 'max-w-[90%] text-[65px] leading-[0.9] font-black tracking-[-0.05em]'
                  : 'max-w-[92%] text-[22px] leading-[0.92] font-black tracking-[-0.04em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase()),
              h('p', {
                class: isExport ? 'mt-8 text-[22px]' : 'mt-4 text-[9px]',
                style: { fontFamily: TEMPLATE_MONO_FONT }
              }, `HEX ${templateHex(c.hex).replace('#', '')}`)
            ]),
            index === 3 ? templateBrand(color, isExport, 'right') : null
          ])
        })
      ])
    }
  }
})

const LinearBarsTemplate = defineComponent({
  name: 'LinearBarsTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const bars = Array.from({ length: 5 }).map((_, i) =>
        templateColor(props.colors, i, { hex: '#111827', name: `Tone ${i + 1}`, roleLabel: `Tone 0${i + 1}` })
      )

      return h('div', {
        class: 'relative h-full w-full flex flex-col overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        ...bars.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'flex-1 flex items-center justify-between',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '0 58px' : '0 18px'
            }
          }, [
            h('div', { class: 'flex items-baseline gap-5' }, [
              h('span', {
                class: isExport ? 'text-[13px] font-bold opacity-70' : 'text-[7px] font-bold opacity-70'
              }, String(index + 1).padStart(2, '0')),
              h('span', {
                class: isExport
                  ? 'text-[38px] leading-none font-black tracking-[-0.04em]'
                  : 'text-[14px] leading-none font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase())
            ]),
            h('span', {
              class: isExport ? 'text-[18px]' : 'text-[8px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        }),
        templateBrand(getTextColor(bars[4].hex), isExport, 'right')
      ])
    }
  }
})

const MegaColumnsTemplate = defineComponent({
  name: 'MegaColumnsTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const cols = Array.from({ length: 5 }).map((_, i) =>
        templateColor(props.colors, i, { hex: '#111827', name: `Column ${i + 1}` })
      )

      return h('div', {
        class: 'relative h-full w-full grid grid-cols-5 overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        ...cols.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'relative flex flex-col justify-between overflow-hidden',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '46px 24px 42px' : '16px 7px 14px'
            }
          }, [
            h('span', {
              class: isExport ? 'text-[12px] font-bold opacity-70' : 'text-[6px] font-bold opacity-70'
            }, String(index + 1).padStart(2, '0')),
            h('div', [
              h('p', {
                class: isExport
                  ? 'origin-bottom-left -rotate-90 translate-x-[34px] translate-y-[-8px] whitespace-nowrap text-[34px] font-black tracking-[-0.03em]'
                  : 'origin-bottom-left -rotate-90 translate-x-[12px] translate-y-[-3px] whitespace-nowrap text-[11px] font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase()),
              h('p', {
                class: isExport ? 'mt-6 text-[13px]' : 'mt-2 text-[6px]',
                style: { fontFamily: TEMPLATE_MONO_FONT }
              }, templateHex(c.hex))
            ])
          ])
        }),
        templateBrand(getTextColor(cols[4].hex), isExport, 'right')
      ])
    }
  }
})

const TagGridTemplate = defineComponent({
  name: 'TagGridTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const tags = Array.from({ length: 6 }).map((_, i) =>
        templateColor(props.colors, i, { hex: '#E5E7EB', name: `Color ${i + 1}`, roleLabel: `Tag ${i + 1}` })
      )
      const bg = '#F4F4F2'

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: {
          backgroundColor: bg,
          fontFamily: TEMPLATE_BODY_FONT,
          padding: isExport ? '72px 68px 76px' : '22px 20px 24px'
        }
      }, [
        h('div', { class: 'flex items-end justify-between' }, [
          h('div', [
            h('p', {
              class: isExport ? 'text-[15px] uppercase tracking-[0.18em] text-zinc-500 font-semibold' : 'text-[7px] uppercase tracking-[0.15em] text-zinc-500 font-semibold'
            }, 'PALETTE INDEX'),
            h('p', {
              class: isExport ? 'mt-3 text-[48px] leading-none font-black text-zinc-950 tracking-[-0.05em]' : 'mt-1 text-[18px] leading-none font-black text-zinc-950 tracking-[-0.04em]',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, 'COLOR SYSTEM')
          ]),
          h('p', {
            class: isExport ? 'text-[15px] text-zinc-500' : 'text-[7px] text-zinc-500',
            style: { fontFamily: TEMPLATE_MONO_FONT }
          }, '06 TONES')
        ]),
        h('div', {
          class: 'grid grid-cols-2',
          style: { gap: isExport ? '24px' : '8px', marginTop: isExport ? '58px' : '18px' }
        }, tags.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'relative flex flex-col justify-between',
            style: {
              minHeight: isExport ? '265px' : '88px',
              backgroundColor: c.hex,
              color,
              borderRadius: isExport ? '32px' : '13px',
              padding: isExport ? '28px 30px 26px' : '10px 11px',
              boxShadow: isExport ? 'none' : '0 14px 34px rgba(15,23,42,.08)'
            }
          }, [
            h('span', {
              class: isExport ? 'text-[12px] font-bold opacity-70' : 'text-[6px] font-bold opacity-70'
            }, String(index + 1).padStart(2, '0')),
            h('div', [
              h('p', {
                class: isExport ? 'text-[30px] leading-none font-black tracking-[-0.04em]' : 'text-[11px] leading-none font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase()),
              h('p', {
                class: isExport ? 'mt-3 text-[14px]' : 'mt-1.5 text-[7px]',
                style: { fontFamily: TEMPLATE_MONO_FONT }
              }, templateHex(c.hex))
            ])
          ])
        })),
        templateBrand('#18181B', isExport, 'right')
      ])
    }
  }
})

const SideLabelTemplate = defineComponent({
  name: 'SideLabelTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const base = templateColor(props.colors, 0, { hex: '#18181B', name: 'Core', roleLabel: 'Primary Base' })
      const others = [
        templateColor(props.colors, 1, { hex: '#C21E56', name: 'Raspberry' }),
        templateColor(props.colors, 2, { hex: '#F4EDE2', name: 'Oat Milk' }),
        templateColor(props.colors, 3, { hex: '#6EA857', name: 'Fresh Basil' })
      ]
      const baseText = getTextColor(base.hex)

      return h('div', {
        class: 'relative h-full w-full grid grid-cols-[0.38fr_0.62fr] overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        h('div', {
          class: 'relative flex flex-col justify-between',
          style: {
            backgroundColor: base.hex,
            color: baseText,
            padding: isExport ? '64px 42px 56px' : '20px 14px 18px'
          }
        }, [
          h('p', { class: templateRoleClass(isExport) }, base.roleLabel || 'Primary Base'),
          h('div', [
            h('p', {
              class: isExport ? 'text-[60px] leading-[0.9] font-black tracking-[-0.05em]' : 'text-[21px] leading-[0.9] font-black tracking-[-0.04em]',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (base.name || 'Core').toUpperCase()),
            h('p', {
              class: isExport ? 'mt-6 text-[18px]' : 'mt-3 text-[8px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(base.hex))
          ])
        ]),
        h('div', { class: 'grid grid-rows-3' }, others.map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'flex items-center justify-between',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '0 44px' : '0 15px'
            }
          }, [
            h('div', [
              h('p', {
                class: isExport ? 'text-[11px] uppercase tracking-[0.16em] font-semibold opacity-75' : 'text-[6px] uppercase tracking-[0.12em] font-semibold opacity-75'
              }, `Support ${index + 1}`),
              h('p', {
                class: isExport ? 'mt-2 text-[34px] leading-none font-black tracking-[-0.04em]' : 'mt-1 text-[12px] leading-none font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase())
            ]),
            h('p', {
              class: isExport ? 'text-[17px]' : 'text-[7px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        })),
        templateBrand(getTextColor(others[2].hex), isExport, 'right')
      ])
    }
  }
})

const SplitBannerTemplate = defineComponent({
  name: 'SplitBannerTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const hero = templateColor(props.colors, 0, { hex: '#FFC344', name: 'Mango Glow', roleLabel: 'Primary Base' })
      const a = templateColor(props.colors, 1, { hex: '#F28C77', name: 'Coral Cloud', roleLabel: 'Accent' })
      const b = templateColor(props.colors, 2, { hex: '#7FBFCE', name: 'Ocean Mist', roleLabel: 'Support' })
      const heroText = getTextColor(hero.hex)

      return h('div', {
        class: 'relative h-full w-full flex flex-col overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        h('div', {
          class: 'relative flex-[0.58] flex flex-col justify-between',
          style: {
            backgroundColor: hero.hex,
            color: heroText,
            padding: isExport ? '72px 70px 58px' : '22px 22px 18px'
          }
        }, [
          h('p', { class: templateRoleClass(isExport) }, hero.roleLabel || 'Primary Base'),
          h('div', [
            h('p', {
              class: isExport ? 'max-w-[82%] text-[88px] leading-[0.86] font-black tracking-[-0.055em]' : 'max-w-[86%] text-[29px] leading-[0.9] font-black tracking-[-0.045em]',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (hero.name || 'Hero Color').toUpperCase()),
            h('p', {
              class: isExport ? 'mt-7 text-[22px]' : 'mt-3 text-[9px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, `HEX ${templateHex(hero.hex).replace('#', '')}`)
          ])
        ]),
        h('div', { class: 'flex-[0.42] grid grid-cols-2' }, [a, b].map(c => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'flex flex-col justify-between',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '42px 38px 36px' : '14px 13px 12px'
            }
          }, [
            h('p', {
              class: isExport ? 'text-[12px] uppercase tracking-[0.16em] font-semibold' : 'text-[6px] uppercase tracking-[0.12em] font-semibold'
            }, c.roleLabel || 'Accent'),
            h('div', [
              h('p', {
                class: isExport ? 'text-[38px] leading-none font-black tracking-[-0.04em]' : 'text-[13px] leading-none font-black tracking-[-0.03em]',
                style: { fontFamily: TEMPLATE_DISPLAY_FONT }
              }, (c.name || 'Color').toUpperCase()),
              h('p', {
                class: isExport ? 'mt-4 text-[15px]' : 'mt-2 text-[7px]',
                style: { fontFamily: TEMPLATE_MONO_FONT }
              }, templateHex(c.hex))
            ])
          ])
        })),
        templateBrand(getTextColor(b.hex), isExport, 'right')
      ])
    }
  }
})

const MasonryTemplate = defineComponent({
  name: 'MasonryTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const colors = Array.from({ length: 6 }).map((_, i) =>
        templateColor(props.colors, i, { hex: '#E5E7EB', name: `Tone ${i + 1}` })
      )

      const block = (c, extraClass, index) => {
        const color = getTextColor(c.hex)
        return h('div', {
          class: `relative flex flex-col justify-between ${extraClass}`,
          style: {
            backgroundColor: c.hex,
            color,
            padding: isExport ? '30px 30px 28px' : '10px',
            borderRadius: isExport ? '30px' : '12px'
          }
        }, [
          h('span', {
            class: isExport ? 'text-[11px] font-bold opacity-70' : 'text-[6px] font-bold opacity-70'
          }, String(index + 1).padStart(2, '0')),
          h('div', [
            h('p', {
              class: isExport ? 'text-[28px] leading-none font-black tracking-[-0.04em]' : 'text-[10px] leading-none font-black tracking-[-0.03em]',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (c.name || 'Color').toUpperCase()),
            h('p', {
              class: isExport ? 'mt-3 text-[13px]' : 'mt-1.5 text-[6px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        ])
      }

      return h('div', {
        class: 'relative h-full w-full overflow-hidden bg-[#F4F4F2]',
        style: {
          padding: isExport ? '56px' : '18px',
          fontFamily: TEMPLATE_BODY_FONT
        }
      }, [
        h('div', {
          class: 'grid h-full grid-cols-12 grid-rows-12',
          style: { gap: isExport ? '18px' : '6px' }
        }, [
          block(colors[0], 'col-span-7 row-span-7', 0),
          block(colors[1], 'col-span-5 row-span-4', 1),
          block(colors[2], 'col-span-5 row-span-3', 2),
          block(colors[3], 'col-span-4 row-span-5', 3),
          block(colors[4], 'col-span-4 row-span-5', 4),
          block(colors[5], 'col-span-4 row-span-5', 5)
        ]),
        templateBrand('#18181B', isExport, 'right')
      ])
    }
  }
})

const CornerAccentTemplate = defineComponent({
  name: 'CornerAccentTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const c1 = templateColor(props.colors, 0, { hex: '#2E3A59', name: 'Blueberry Deep' })
      const c2 = templateColor(props.colors, 1, { hex: '#C8C1D9', name: 'Frosted Lilac' })
      const c3 = templateColor(props.colors, 2, { hex: '#7C8DA6', name: 'Soft Denim' })
      const c4 = templateColor(props.colors, 3, { hex: '#E9EEF4', name: 'Mist' })
      const centerText = getTextColor(c2.hex)

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: {
          background: `linear-gradient(135deg, ${c1.hex}, ${c3.hex})`,
          fontFamily: TEMPLATE_BODY_FONT
        }
      }, [
        h('div', {
          class: 'absolute left-0 top-0',
          style: { width: '34%', height: '28%', backgroundColor: c1.hex }
        }),
        h('div', {
          class: 'absolute right-0 top-0',
          style: { width: '28%', height: '34%', backgroundColor: c4.hex }
        }),
        h('div', {
          class: 'absolute bottom-0 left-0',
          style: { width: '29%', height: '35%', backgroundColor: c3.hex }
        }),
        h('div', {
          class: 'absolute bottom-0 right-0',
          style: { width: '36%', height: '29%', backgroundColor: c1.hex }
        }),
        h('div', {
          class: `absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center ${isExport ? '' : 'shadow-2xl'}`,
          style: {
            width: isExport ? '62%' : '67%',
            height: isExport ? '42%' : '44%',
            backgroundColor: c2.hex,
            color: centerText,
            borderRadius: isExport ? '52px' : '20px',
            padding: isExport ? '44px' : '16px'
          }
        }, [
          h('p', {
            class: isExport ? 'text-[12px] uppercase tracking-[0.18em] font-semibold' : 'text-[7px] uppercase tracking-[0.14em] font-semibold'
          }, 'FEATURE COLOR'),
          h('p', {
            class: isExport ? 'mt-4 text-[68px] leading-[0.9] font-black tracking-[-0.05em]' : 'mt-2 text-[23px] leading-[0.9] font-black tracking-[-0.04em]',
            style: { fontFamily: TEMPLATE_DISPLAY_FONT }
          }, (c2.name || 'Feature').toUpperCase()),
          h('p', {
            class: isExport ? 'mt-5 text-[20px]' : 'mt-2 text-[8px]',
            style: { fontFamily: TEMPLATE_MONO_FONT }
          }, templateHex(c2.hex))
        ]),
        templateBrand('#FFFFFF', isExport, 'center')
      ])
    }
  }
})

const GradientStripTemplate = defineComponent({
  name: 'GradientStripTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const c1 = templateColor(props.colors, 0, { hex: '#2E3A59', name: 'Midnight Blue' })
      const c2 = templateColor(props.colors, 1, { hex: '#7C8DA6', name: 'Soft Denim' })
      const c3 = templateColor(props.colors, 2, { hex: '#C8C1D9', name: 'Frosted Lilac' })

      return h('div', {
        class: 'relative h-full w-full flex flex-col overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT, backgroundColor: '#09090B' }
      }, [
        h('div', {
          class: 'relative flex-[0.67] flex flex-col justify-between',
          style: {
            padding: isExport ? '72px 66px 56px' : '22px 20px 18px',
            background: `linear-gradient(135deg, ${c1.hex} 0%, ${c2.hex} 52%, ${c3.hex} 100%)`
          }
        }, [
          h('p', {
            class: isExport ? 'text-[14px] uppercase tracking-[0.2em] text-white/80 font-semibold' : 'text-[7px] uppercase tracking-[0.16em] text-white/80 font-semibold'
          }, 'GRADIENT STORY'),
          h('div', [
            h('p', {
              class: isExport ? 'max-w-[80%] text-[78px] leading-[0.88] font-black tracking-[-0.05em] text-white' : 'max-w-[84%] text-[26px] leading-[0.9] font-black tracking-[-0.04em] text-white',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (c1.name || 'Gradient').toUpperCase()),
            h('p', {
              class: isExport ? 'mt-6 text-[18px] text-white/80' : 'mt-3 text-[8px] text-white/80',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, `${templateHex(c1.hex)}  →  ${templateHex(c3.hex)}`)
          ])
        ]),
        h('div', { class: 'flex-[0.33] grid grid-cols-3' }, [c1, c2, c3].map((c, index) => {
          const color = getTextColor(c.hex)
          return h('div', {
            class: 'flex flex-col justify-end',
            style: {
              backgroundColor: c.hex,
              color,
              padding: isExport ? '30px 28px 26px' : '10px'
            }
          }, [
            h('p', {
              class: isExport ? 'text-[10px] uppercase tracking-[0.14em] font-semibold' : 'text-[6px] uppercase tracking-[0.10em] font-semibold'
            }, ['Start', 'Middle', 'End'][index]),
            h('p', {
              class: isExport ? 'mt-2 text-[20px] font-black tracking-[-0.03em]' : 'mt-1 text-[8px] font-bold',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (c.name || 'Color').toUpperCase()),
            h('p', {
              class: isExport ? 'mt-2 text-[13px]' : 'mt-1 text-[6px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        })),
        templateBrand(getTextColor(c3.hex), isExport, 'right')
      ])
    }
  }
})

const ContrastBlocksTemplate = defineComponent({
  name: 'ContrastBlocksTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const c1 = templateColor(props.colors, 0, { hex: '#202938', name: 'Midnight Stone' })
      const c2 = templateColor(props.colors, 1, { hex: '#F4EDE2', name: 'Oat Milk' })
      const c3 = templateColor(props.colors, 2, { hex: '#C21E56', name: 'Raspberry' })
      const c4 = templateColor(props.colors, 3, { hex: '#6EA857', name: 'Fresh Basil' })

      const bigBlock = (c, label) => {
        const color = getTextColor(c.hex)
        return h('div', {
          class: 'flex flex-col justify-between',
          style: {
            backgroundColor: c.hex,
            color,
            padding: isExport ? '58px 52px 48px' : '18px 16px 15px'
          }
        }, [
          h('p', {
            class: isExport ? 'text-[13px] uppercase tracking-[0.18em] font-semibold' : 'text-[7px] uppercase tracking-[0.14em] font-semibold'
          }, label),
          h('div', [
            h('p', {
              class: isExport ? 'text-[52px] leading-[0.9] font-black tracking-[-0.045em]' : 'text-[18px] leading-[0.9] font-black tracking-[-0.04em]',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (c.name || label).toUpperCase()),
            h('p', {
              class: isExport ? 'mt-5 text-[18px]' : 'mt-2 text-[8px]',
              style: { fontFamily: TEMPLATE_MONO_FONT }
            }, templateHex(c.hex))
          ])
        ])
      }

      const smallBlock = (c, label) => {
        const color = getTextColor(c.hex)
        return h('div', {
          class: 'flex items-end justify-between',
          style: {
            backgroundColor: c.hex,
            color,
            padding: isExport ? '28px 30px 26px' : '10px'
          }
        }, [
          h('div', [
            h('p', {
              class: isExport ? 'text-[10px] uppercase tracking-[0.14em] font-semibold' : 'text-[6px] uppercase tracking-[0.10em] font-semibold'
            }, label),
            h('p', {
              class: isExport ? 'mt-2 text-[24px] font-black tracking-[-0.03em]' : 'mt-1 text-[9px] font-bold',
              style: { fontFamily: TEMPLATE_DISPLAY_FONT }
            }, (c.name || label).toUpperCase())
          ]),
          h('p', {
            class: isExport ? 'text-[14px]' : 'text-[6px]',
            style: { fontFamily: TEMPLATE_MONO_FONT }
          }, templateHex(c.hex))
        ])
      }

      return h('div', {
        class: 'relative h-full w-full grid grid-rows-[0.7fr_0.3fr] overflow-hidden',
        style: { fontFamily: TEMPLATE_BODY_FONT }
      }, [
        h('div', { class: 'grid grid-cols-2' }, [
          bigBlock(c1, 'Dark Base'),
          bigBlock(c2, 'Light Base')
        ]),
        h('div', { class: 'grid grid-cols-2' }, [
          smallBlock(c3, 'Accent 01'),
          smallBlock(c4, 'Accent 02')
        ]),
        templateBrand(getTextColor(c4.hex), isExport, 'right')
      ])
    }
  }
})

const HeroLabelTemplate = defineComponent({
  name: 'HeroLabelTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const main = templateColor(props.colors, 0, { hex: '#2E3A59', name: 'Blueberry Deep', roleLabel: 'Hero Color' })
      const accent = templateColor(props.colors, 1, { hex: '#C8C1D9', name: 'Frosted Lilac', roleLabel: 'Accent' })
      const mainText = getTextColor(main.hex)
      const accentText = getTextColor(accent.hex)

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: {
          background: `linear-gradient(145deg, ${main.hex} 0%, ${main.hex} 62%, ${accent.hex} 180%)`,
          fontFamily: TEMPLATE_BODY_FONT
        }
      }, [
        h('div', {
          class: `absolute left-1/2 top-[44%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center ${isExport ? '' : 'shadow-2xl'}`,
          style: {
            width: isExport ? '74%' : '76%',
            minHeight: isExport ? '420px' : '145px',
            backgroundColor: main.hex,
            color: mainText,
            borderRadius: isExport ? '58px' : '22px',
            padding: isExport ? '58px 72px' : '18px 20px',
            boxShadow: isExport ? 'none' : '0 34px 70px rgba(0,0,0,.30), inset 0 1px 0 rgba(255,255,255,.16)'
          }
        }, [
          h('p', {
            class: isExport ? 'text-[13px] uppercase tracking-[0.18em] font-semibold opacity-80' : 'text-[7px] uppercase tracking-[0.14em] font-semibold opacity-80'
          }, main.roleLabel || 'Hero Color'),
          h('p', {
            class: isExport ? 'mt-5 text-[84px] leading-[0.86] font-black tracking-[-0.055em]' : 'mt-2 text-[28px] leading-[0.9] font-black tracking-[-0.045em]',
            style: { fontFamily: TEMPLATE_DISPLAY_FONT }
          }, (main.name || 'Hero').toUpperCase()),
          h('p', {
            class: isExport ? 'mt-6 text-[22px]' : 'mt-3 text-[9px]',
            style: { fontFamily: TEMPLATE_MONO_FONT }
          }, `HEX ${templateHex(main.hex)}`)
        ]),
        h('div', {
          class: 'absolute inset-x-0 bottom-0 flex items-center justify-between',
          style: {
            height: isExport ? '190px' : '66px',
            backgroundColor: accent.hex,
            color: accentText,
            padding: isExport ? '0 58px' : '0 18px'
          }
        }, [
          h('span', {
            class: isExport ? 'text-[16px] uppercase tracking-[0.18em] font-semibold' : 'text-[7px] uppercase tracking-[0.14em] font-semibold'
          }, (accent.name || 'Accent').toUpperCase()),
          h('span', {
            class: isExport ? 'text-[19px]' : 'text-[8px]',
            style: { fontFamily: TEMPLATE_MONO_FONT }
          }, templateHex(accent.hex))
        ]),
        templateBrand(accentText, isExport, 'center')
      ])
    }
  }
})


const SpotlightTemplate = defineComponent({
  name: 'SpotlightTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const cards = [
        templateColor(props.colors, 0, { hex: '#5D7CA6', name: 'Summit Blue', roleLabel: 'Primary Base' }),
        templateColor(props.colors, 1, { hex: '#CED6DD', name: 'Mist Grey', roleLabel: 'Secondary Base' }),
        templateColor(props.colors, 2, { hex: '#2B3644', name: 'Midnight Stone', roleLabel: 'Dark Accent' })
      ]

      const bg1 = cards[0].hex
      const bg2 = cards[1].hex
      const bg3 = cards[2].hex

      const titleSize = name => {
        const length = String(name || '').replace(/\s+/g, '').length
        if (isExport) {
          if (length >= 18) return '45px'
          if (length >= 15) return '50px'
          return '58px'
        }
        if (length >= 18) return '17px'
        if (length >= 15) return '19px'
        return '22px'
      }

      const renderCard = (c, index) => {
        const color = getTextColor(c.hex)
        const radius = isExport ? 54 : 22

        const exportDepth = isExport
          ? h('img', {
              src: createExportCardShadowDataUrl(468, 308, radius),
              alt: '',
              'aria-hidden': 'true',
              style: {
                position: 'absolute',
                left: '-105px',
                top: '-70px',
                width: '678px',
                height: '503px',
                zIndex: 0,
                pointerEvents: 'none',
                maxWidth: 'none'
              }
            })
          : null

        const exportSurface = isExport
          ? h('svg', {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '100%',
              height: '100%',
              preserveAspectRatio: 'none',
              'aria-hidden': 'true',
              style: {
                position: 'absolute',
                inset: '0',
                width: '100%',
                height: '100%',
                zIndex: 1,
                pointerEvents: 'none',
                overflow: 'visible'
              }
            }, [
              h('rect', {
                x: 0,
                y: 0,
                width: '100%',
                height: '100%',
                rx: radius,
                ry: radius,
                fill: c.hex
              })
            ])
          : null

        return h('div', {
          class: 'flex flex-col items-center justify-center text-center',
          style: {
            position: 'relative',
            isolation: 'isolate',
            width: isExport ? '468px' : '43.4%',
            height: isExport ? '308px' : '22.8%',
            minHeight: isExport ? '308px' : '0',
            flex: '0 0 auto',
            boxSizing: 'border-box',
            backgroundColor: isExport ? 'transparent' : c.hex,
            color,
            borderRadius: `${radius}px`,
            overflow: isExport ? 'visible' : 'hidden',
            boxShadow: isExport
              ? 'none'
              : '0 12px 22px rgba(0,0,0,.23), 0 4px 8px rgba(0,0,0,.10)',
            transform: 'none',
            zIndex: 2 + index
          }
        }, [
          exportDepth,
          exportSurface,
          h('p', {
            style: {
              fontFamily: TEMPLATE_DISPLAY_FONT,
              fontSize: titleSize(c.name),
              fontWeight: '900',
              lineHeight: '0.90',
              letterSpacing: '-0.045em',
              maxWidth: '90%',
              whiteSpace: 'nowrap',
              position: 'relative',
              zIndex: 2
            }
          }, (c.name || 'Color').toUpperCase()),
          h('p', {
            style: {
              marginTop: isExport ? '18px' : '5px',
              fontFamily: TEMPLATE_BODY_FONT,
              fontSize: isExport ? '24px' : '9px',
              lineHeight: '1',
              fontWeight: '500',
              letterSpacing: '-0.015em',
              opacity: 0.95,
              position: 'relative',
              zIndex: 2
            }
          }, `HEX ${templateHex(c.hex)}`)
        ].filter(Boolean))
      }

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: {
          fontFamily: TEMPLATE_BODY_FONT,
          backgroundColor: bg3
        }
      }, [
        h('div', {
          class: 'absolute inset-0',
          style: {
            background: `
              radial-gradient(circle at 14% 22%, ${bg2}f0 0%, transparent 30%),
              radial-gradient(circle at 84% 20%, ${bg1}cc 0%, transparent 34%),
              radial-gradient(circle at 50% 76%, ${bg3}f4 0%, transparent 46%),
              linear-gradient(160deg, ${bg2} 0%, ${bg1} 48%, ${bg3} 100%)
            `,
            transform: isExport ? 'scale(1.04)' : 'scale(1.05)',
            transformOrigin: 'center center',
            filter: isExport ? 'none' : 'blur(8px)'
          }
        }),
        h('div', {
          class: 'absolute inset-0 pointer-events-none',
          style: {
            background: 'linear-gradient(180deg, rgba(255,255,255,.08) 0%, rgba(255,255,255,.015) 48%, rgba(0,0,0,.22) 100%)'
          }
        }),
        h('div', {
          class: 'relative z-10 h-full w-full flex flex-col items-center justify-center',
          style: {
            gap: isExport ? '44px' : '3.3%'
          }
        }, cards.map(renderCard)),
        h('div', {
          class: 'absolute left-1/2 -translate-x-1/2 z-20 font-black uppercase',
          style: {
            bottom: isExport ? '30px' : '2.2%',
            color: '#FFFFFF',
            fontFamily: TEMPLATE_DISPLAY_FONT,
            fontSize: isExport ? '27px' : '10px',
            fontWeight: '900',
            lineHeight: '1',
            letterSpacing: '-0.045em',
            textShadow: '0 2px 7px rgba(0,0,0,.40)'
          }
        },)
      ])
    }
  }
})

const PhotoCardStackTemplate = defineComponent({
  name: 'PhotoCardStackTemplate',
  props: {
    colors: { type: Array, required: true },
    isExport: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const cards = [
        templateColor(props.colors, 0, { hex: '#5D7CA6', name: 'Summit Blue', roleLabel: 'Top Note' }),
        templateColor(props.colors, 1, { hex: '#CED6DD', name: 'Mist Grey', roleLabel: 'Core Note' }),
        templateColor(props.colors, 2, { hex: '#2B3644', name: 'Midnight Stone', roleLabel: 'Anchor Note' })
      ]

      const background = getPhotoBackgroundPreset(cards)
      const repeatedLayerValue = value => Array(background.layerCount || 1).fill(value).join(', ')

      const titleSize = name => {
        const length = String(name || '').replace(/\s+/g, '').length
        if (isExport) {
          if (length >= 18) return '45px'
          if (length >= 15) return '50px'
          return '58px'
        }
        if (length >= 18) return '17px'
        if (length >= 15) return '19px'
        return '22px'
      }

      const renderCard = (c, index) => {
        const color = getTextColor(c.hex)
        const radius = isExport ? 54 : 22

        const exportDepth = isExport
          ? h('img', {
              src: createExportCardShadowDataUrl(468, 308, radius),
              alt: '',
              'aria-hidden': 'true',
              style: {
                position: 'absolute',
                left: '-105px',
                top: '-70px',
                width: '678px',
                height: '503px',
                zIndex: 0,
                pointerEvents: 'none',
                maxWidth: 'none'
              }
            })
          : null

        const exportSurface = isExport
          ? h('svg', {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '100%',
              height: '100%',
              preserveAspectRatio: 'none',
              'aria-hidden': 'true',
              style: {
                position: 'absolute',
                inset: '0',
                width: '100%',
                height: '100%',
                zIndex: 1,
                pointerEvents: 'none',
                overflow: 'visible'
              }
            }, [
              h('rect', {
                x: 0,
                y: 0,
                width: '100%',
                height: '100%',
                rx: radius,
                ry: radius,
                fill: c.hex
              })
            ])
          : null

        return h('div', {
          class: 'flex flex-col items-center justify-center text-center',
          style: {
            position: 'relative',
            isolation: 'isolate',
            width: isExport ? '468px' : '43.4%',
            height: isExport ? '308px' : '22.8%',
            minHeight: isExport ? '308px' : '0',
            flex: '0 0 auto',
            boxSizing: 'border-box',
            backgroundColor: isExport ? 'transparent' : c.hex,
            color,
            borderRadius: `${radius}px`,
            overflow: isExport ? 'visible' : 'hidden',
            boxShadow: isExport
              ? 'none'
              : '0 12px 22px rgba(0,0,0,.23), 0 4px 8px rgba(0,0,0,.10)',
            transform: 'none',
            zIndex: 2 + index
          }
        }, [
          exportDepth,
          exportSurface,
          h('p', {
            style: {
              fontFamily: TEMPLATE_DISPLAY_FONT,
              fontSize: titleSize(c.name),
              fontWeight: '900',
              lineHeight: '0.90',
              letterSpacing: '-0.045em',
              maxWidth: '90%',
              whiteSpace: 'nowrap',
              position: 'relative',
              zIndex: 2
            }
          }, (c.name || 'Color').toUpperCase()),
          h('p', {
            style: {
              marginTop: isExport ? '18px' : '5px',
              fontFamily: TEMPLATE_BODY_FONT,
              fontSize: isExport ? '24px' : '9px',
              lineHeight: '1',
              fontWeight: '500',
              letterSpacing: '-0.015em',
              opacity: 0.95,
              position: 'relative',
              zIndex: 2
            }
          }, `HEX ${templateHex(c.hex)}`)
        ].filter(Boolean))
      }

      const exportBackgroundImage = isExport && photoStackExportBackground.value
        ? `url("${photoStackExportBackground.value}")`
        : ''

      return h('div', {
        class: 'relative h-full w-full overflow-hidden',
        style: {
          fontFamily: TEMPLATE_BODY_FONT,
          backgroundColor: '#101418'
        }
      }, [
        h('div', {
          class: 'absolute inset-0',
          style: {
            backgroundImage: isExport
              ? (exportBackgroundImage || background.cssBackground)
              : background.cssBackground,
            backgroundSize: isExport ? 'cover' : repeatedLayerValue('cover'),
            backgroundPosition: isExport
              ? (background.position || 'center center')
              : repeatedLayerValue(background.position || 'center center'),
            backgroundRepeat: isExport ? 'no-repeat' : repeatedLayerValue('no-repeat'),
            transform: isExport ? 'none' : 'scale(1.035)',
            transformOrigin: 'center center',
            filter: isExport ? 'none' : 'blur(3px)'
          }
        }),

        h('div', {
          class: 'absolute inset-0 pointer-events-none z-[1]',
          style: {
            background: isExport && exportBackgroundImage ? background.overlay : 'transparent'
          }
        }),

        h('div', {
          class: 'absolute inset-0 pointer-events-none z-[1]',
          style: {
            background: 'linear-gradient(180deg, rgba(255,255,255,0.015) 0%, rgba(255,255,255,0) 72%, rgba(0,0,0,0.16) 100%)'
          }
        }),

        h('div', {
          class: 'relative z-10 h-full w-full flex flex-col items-center justify-center',
          style: {
            gap: isExport ? '44px' : '3.3%'
          }
        }, cards.map(renderCard)),

        h('div', {
          class: 'absolute left-1/2 -translate-x-1/2 z-20 font-black uppercase',
          style: {
            bottom: isExport ? '30px' : '2.2%',
            color: '#FFFFFF',
            fontFamily: TEMPLATE_DISPLAY_FONT,
            fontSize: isExport ? '27px' : '10px',
            fontWeight: '900',
            lineHeight: '1',
            letterSpacing: '-0.045em',
            textShadow: '0 2px 7px rgba(0,0,0,.40)'
          }
        },)
      ].filter(Boolean))
    }
  }
})

const templates = [
  {
    id: 'duo',
    name: 'Editorial Duo',
    aspect: '2 colors',
    description: 'Two bold editorial fields with premium type hierarchy.',
    component: DuoSplitTemplate
  },
  {
    id: 'neon-grid',
    name: 'Night Editorial',
    aspect: '5 colors',
    description: 'Dark hero header with a refined four-color editorial grid.',
    component: NeonGridTemplate
  },
  {
    id: 'triple-stripe',
    name: 'Type Stripes',
    aspect: '3 colors',
    description: 'Three strong horizontal fields with names and HEX details.',
    component: TripleStripeTemplate
  },
  {
    id: 'card-stack',
    name: 'Editorial Stack',
    aspect: '5 colors',
    description: 'Five refined stacked color cards for full palette systems.',
    component: CardStackTemplate
  },
  {
    id: 'spotlight',
    name: 'Atmosphere Cards',
    aspect: '3 colors',
    description: 'Three floating color cards over a soft cinematic background.',
    component: SpotlightTemplate
  },
  {
    id: 'photo-card-stack',
    name: 'Photo Stack',
    aspect: '3 colors',
    description: 'Theme-matched editorial photo background with stacked premium swatches.',
    component: PhotoCardStackTemplate
  },
  {
    id: 'quadrant',
    name: 'Editorial Quadrants',
    aspect: '4 colors',
    description: 'A premium 2x2 color system inspired by editorial brand boards.',
    component: QuadrantTemplate
  },
  {
    id: 'linear-bars',
    name: 'Color Ledger',
    aspect: '5 colors',
    description: 'Structured color rows with clear hierarchy and technical detail.',
    component: LinearBarsTemplate
  },
  {
    id: 'mega-columns',
    name: 'Poster Columns',
    aspect: '5 colors',
    description: 'Tall color columns designed like a contemporary poster system.',
    component: MegaColumnsTemplate
  },
  {
    id: 'tag-grid',
    name: 'Swatch Index',
    aspect: '6 colors',
    description: 'A clean indexed grid of rounded professional swatches.',
    component: TagGridTemplate
  },
  {
    id: 'side-label',
    name: 'Editorial Index',
    aspect: '4 colors',
    description: 'Primary color rail with a structured supporting palette.',
    component: SideLabelTemplate
  },
  {
    id: 'split-banner',
    name: 'Campaign Banner',
    aspect: '3 colors',
    description: 'Large campaign hero color with two supporting color fields.',
    component: SplitBannerTemplate
  },
  {
    id: 'masonry',
    name: 'Masonry Editorial',
    aspect: '6 colors',
    description: 'A refined asymmetrical palette board with editorial rhythm.',
    component: MasonryTemplate
  },
  {
    id: 'corner-accent',
    name: 'Focus Frame',
    aspect: '4 colors',
    description: 'A central feature card framed by supporting corner colors.',
    component: CornerAccentTemplate
  },
  {
    id: 'gradient-strip',
    name: 'Gradient Story',
    aspect: '3 colors',
    description: 'A large gradient story with technical swatches underneath.',
    component: GradientStripTemplate
  },
  {
    id: 'contrast-blocks',
    name: 'Contrast Study',
    aspect: '4 colors',
    description: 'Dark and light foundations paired with two focused accents.',
    component: ContrastBlocksTemplate
  },
  {
    id: 'hero-label',
    name: 'Hero Card',
    aspect: '2 colors',
    description: 'A premium hero card with one strong supporting accent.',
    component: HeroLabelTemplate
  }
]

const templateIdToEnum = {
  duo: 'DUO',
  'neon-grid': 'NEON_GRID',
  'triple-stripe': 'TRIPLE_STRIPE',
  'card-stack': 'CARD_STACK',
  quadrant: 'QUADRANT',
  'linear-bars': 'LINEAR_BARS',
  'center-burst': 'CENTER_BURST',
  'mega-columns': 'MEGA_COLUMNS',
  'pill-row': 'PILL_ROW',
  'tag-grid': 'TAG_GRID',
  spotlight: 'SPOTLIGHT',
  'side-label': 'SIDE_LABEL',
  'split-banner': 'SPLIT_BANNER',
  masonry: 'MASONRY',
  'corner-accent': 'CORNER_ACCENT',
  timeline: 'TIMELINE',
  'gradient-strip': 'GRADIENT_STRIP',
  'badge-cluster': 'BADGE_CLUSTER',
  'contrast-blocks': 'CONTRAST_BLOCKS',
  'hero-label': 'HERO_LABEL'
}

const enumToTemplateId = {
  DUO: 'duo',
  NEON_GRID: 'neon-grid',
  TRIPLE_STRIPE: 'triple-stripe',
  CARD_STACK: 'card-stack',
  QUADRANT: 'quadrant',
  LINEAR_BARS: 'linear-bars',
  CENTER_BURST: 'center-burst',
  MEGA_COLUMNS: 'mega-columns',
  PILL_ROW: 'pill-row',
  TAG_GRID: 'tag-grid',
  SPOTLIGHT: 'spotlight',
  SIDE_LABEL: 'side-label',
  SPLIT_BANNER: 'split-banner',
  MASONRY: 'masonry',
  CORNER_ACCENT: 'corner-accent',
  TIMELINE: 'timeline',
  GRADIENT_STRIP: 'gradient-strip',
  BADGE_CLUSTER: 'badge-cluster',
  CONTRAST_BLOCKS: 'contrast-blocks',
  HERO_LABEL: 'hero-label'
}

const paletteCombos = [
  {
    "id": "mono-1",
    "name": "Nimbus Corridor",
    "colors": ["#1D090B", "#341114", "#72242C", "#BD3C49", "#D47680", "#DB8E95"]
  },
  {
    "id": "mono-2",
    "name": "Solar Station",
    "colors": ["#0A0E16", "#11192B", "#213A62", "#315B99", "#6A84C7", "#94A8DD"]
  },
  {
    "id": "mono-3",
    "name": "Ink Echo",
    "colors": ["#0C0A14", "#151128", "#2C2354", "#443581", "#6E67B1", "#8A83C4"]
  },
  {
    "id": "mono-4",
    "name": "Arctic Drift",
    "colors": ["#060A0E", "#0D141D", "#1B2D41", "#2A4666", "#5B7994", "#7F9FB1"]
  },
  {
    "id": "mono-5",
    "name": "Copper Crown",
    "colors": ["#0E0804", "#1E1108", "#4A2A14", "#76421F", "#B07B58", "#D0A27E"]
  },
  {
    "id": "mono-6",
    "name": "Quartz Grove",
    "colors": ["#0D0C0D", "#19161A", "#3C343E", "#5F5162", "#9B8BA0", "#BDB0C1"]
  },
  {
    "id": "mono-7",
    "name": "Juniper Canopy",
    "colors": ["#050B08", "#0B1611", "#173427", "#23513D", "#4B7F67", "#71A68E"]
  },
  {
    "id": "mono-8",
    "name": "Obsidian Vault",
    "colors": ["#0A0A0B", "#141417", "#2E2E36", "#484854", "#7E7E8E", "#A3A3B1"]
  },
  {
    "id": "mono-9",
    "name": "Velvet Bridge",
    "colors": ["#0C0711", "#160D22", "#30194B", "#4A2675", "#7B5CA9", "#9A7FC3"]
  },
  {
    "id": "mono-10",
    "name": "Berry Quarry",
    "colors": ["#130609", "#240D12", "#521D2A", "#802C42", "#B06B7A", "#C98EA0"]
  },
  {
    "id": "mono-11",
    "name": "Slate Ribbon",
    "colors": ["#070A0F", "#0F1520", "#1F2E48", "#2F476F", "#5E78A6", "#86A0C8"]
  },
  {
    "id": "mono-12",
    "name": "Cinder Monolith",
    "colors": ["#0B0908", "#171210", "#352A25", "#52423A", "#877468", "#AD9B8E"]
  },
  {
    "id": "mono-13",
    "name": "Mist Ladder",
    "colors": ["#070A0A", "#101616", "#223131", "#344C4C", "#617C7C", "#87A5A5"]
  },
  {
    "id": "mono-14",
    "name": "Lilac Station",
    "colors": ["#0D0810", "#1A1021", "#38224B", "#553474", "#8665A6", "#A88CC2"]
  },
  {
    "id": "mono-15",
    "name": "Cobalt Grove",
    "colors": ["#070915", "#0E1230", "#1E2C6A", "#2E46A4", "#6B79C9", "#98A0DE"]
  },
  {
    "id": "mono-16",
    "name": "Saffron Horizon",
    "colors": ["#0E0A03", "#1E1406", "#4A2F0D", "#754815", "#AF7C3C", "#D4A964"]
  },
  {
    "id": "mono-17",
    "name": "Glacier Vista",
    "colors": ["#060A0F", "#0D1420", "#1B2E48", "#294770", "#5979A8", "#80A1CB"]
  },
  {
    "id": "mono-18",
    "name": "Orchid Studio",
    "colors": ["#0E0711", "#1C0E23", "#3E1C4E", "#602A78", "#9560AF", "#B487CA"]
  },
  {
    "id": "mono-19",
    "name": "Cedar Chamber",
    "colors": ["#0C0805", "#19110A", "#3A2916", "#594123", "#90735A", "#B39A7F"]
  },
  {
    "id": "mono-20",
    "name": "Pearl Atlas",
    "colors": ["#0D0D0C", "#191915", "#3B3A33", "#5D5B51", "#9A9788", "#BCB9AB"]
  },
  {
    "id": "mono-21",
    "name": "Auburn Runway",
    "colors": ["#110705", "#210E0A", "#4D1F14", "#782E1F", "#B26A58", "#D1917D"]
  },
  {
    "id": "mono-22",
    "name": "Marble Echo",
    "colors": ["#0B0B0D", "#16161B", "#34343F", "#51515F", "#88889A", "#AEAEBC"]
  },
  {
    "id": "mono-23",
    "name": "Moss Quarry",
    "colors": ["#050A06", "#0B140D", "#18301F", "#244C31", "#4C7B5E", "#72A386"]
  },
  {
    "id": "mono-24",
    "name": "Rose Cascade",
    "colors": ["#12070B", "#220E15", "#4F1E33", "#7B2D50", "#B06A84", "#CD91A6"]
  },
  {
    "id": "mono-25",
    "name": "Graphite Vault",
    "colors": ["#070809", "#101214", "#23282B", "#363E42", "#636D72", "#879398"]
  },
  {
    "id": "mono-26",
    "name": "Teal Horizon",
    "colors": ["#040B0C", "#0A1619", "#16343A", "#225258", "#4C8086", "#72A8AD"]
  },
  {
    "id": "mono-27",
    "name": "Plum Station",
    "colors": ["#0E0610", "#1B0B21", "#3B174B", "#5B2375", "#8F58AB", "#AF7FC6"]
  },
  {
    "id": "mono-28",
    "name": "Citrine Ribbon",
    "colors": ["#0C0B03", "#171507", "#353014", "#514A20", "#877A4F", "#AB9F73"]
  },
  {
    "id": "mono-29",
    "name": "Nimbus Studio",
    "colors": ["#070A0D", "#0E141E", "#1C2D43", "#2A4767", "#5B7A9C", "#83A2BE"]
  },
  {
    "id": "mono-30",
    "name": "Raven Bridge",
    "colors": ["#07070A", "#111018", "#26223A", "#3B345B", "#6E6296", "#9084B1"]
  },
  {
    "id": "mono-31",
    "name": "Caspian Station",
    "colors": ["#04080C", "#091218", "#143044", "#1E4E6F", "#4E7FA4", "#7CAAD0"]
  },
  {
    "id": "mono-32",
    "name": "Umber Runway",
    "colors": ["#0E0804", "#1C1008", "#402513", "#63391D", "#966E54", "#B9967D"]
  },
  {
    "id": "mono-33",
    "name": "Harbor Ribbon",
    "colors": ["#050A0E", "#0B131F", "#162D49", "#214873", "#4F7CAB", "#79A6CD"]
  },
  {
    "id": "mono-34",
    "name": "Frost Atlas",
    "colors": ["#070A0A", "#101616", "#223232", "#344D4D", "#618080", "#87A8A8"]
  },
  {
    "id": "mono-35",
    "name": "Honey Quarry",
    "colors": ["#100B03", "#1E1607", "#433414", "#66501F", "#9A854E", "#BEAA76"]
  },
  {
    "id": "mono-36",
    "name": "Violet Crown",
    "colors": ["#0F0711", "#1E0E23", "#3F1C4E", "#612A79", "#9760B0", "#B889CB"]
  },
  {
    "id": "mono-37",
    "name": "Basil Harbor",
    "colors": ["#050A06", "#0B140D", "#18301F", "#244C31", "#4C7B5E", "#72A386"]
  },
  {
    "id": "mono-38",
    "name": "Storm Bridge",
    "colors": ["#06080D", "#0D1320", "#1A2A47", "#26406E", "#546FA5", "#7D97C8"]
  },
  {
    "id": "mono-39",
    "name": "Canyon Chamber",
    "colors": ["#110705", "#220E0A", "#522114", "#7F311E", "#B56D58", "#D4957D"]
  },
  {
    "id": "mono-40",
    "name": "Petal Vista",
    "colors": ["#12070C", "#220E18", "#4F1E3A", "#7A2D5B", "#AE6A93", "#CB91B3"]
  },
  {
    "id": "mono-41",
    "name": "Mint Chamber",
    "colors": ["#050B09", "#0B1613", "#17342C", "#235148", "#4B7F76", "#71A69C"]
  },
  {
    "id": "mono-42",
    "name": "Sepia Runway",
    "colors": ["#0E0805", "#1C100B", "#402517", "#633922", "#966E5A", "#B99682"]
  },
  {
    "id": "mono-43",
    "name": "Cocoa Vault",
    "colors": ["#0F0808", "#1D1010", "#422424", "#653838", "#9A6B6B", "#BE9393"]
  },
  {
    "id": "mono-44",
    "name": "Cerulean Vista",
    "colors": ["#04090F", "#091324", "#133052", "#1D4C7F", "#4D7DB2", "#7AA5D1"]
  },
  {
    "id": "mono-45",
    "name": "Beryl Grove",
    "colors": ["#040B08", "#081612", "#103428", "#175141", "#3F7F6A", "#67A792"]
  },
  {
    "id": "mono-46",
    "name": "Wisteria Echo",
    "colors": ["#0D0810", "#1A1021", "#38224B", "#553474", "#8665A6", "#A88CC2"]
  },
  {
    "id": "mono-47",
    "name": "Tarmac Station",
    "colors": ["#070808", "#121313", "#282A2A", "#3E4040", "#6E7070", "#949696"]
  },
  {
    "id": "mono-48",
    "name": "Aurora Grove",
    "colors": ["#050B08", "#0A1611", "#143425", "#1E513A", "#468064", "#6EA88C"]
  },
  {
    "id": "mono-49",
    "name": "Garnet Crown",
    "colors": ["#120507", "#220A0E", "#4F1521", "#7A2033", "#AE5A6A", "#CB808E"]
  },
  {
    "id": "mono-50",
    "name": "Linen Vista",
    "colors": ["#0D0D0B", "#191915", "#3A3A31", "#5B5B4D", "#989880", "#BABAA5"]
  },
  {
    "id": "mono-51",
    "name": "Fig Chamber",
    "colors": ["#0E0810", "#1A1021", "#38224B", "#553474", "#8665A6", "#A88CC2"]
  },
  {
    "id": "mono-52",
    "name": "Silent Horizon",
    "colors": ["#070A0E", "#101626", "#223255", "#334E83", "#6D7FB4", "#98A4D1"]
  },
  {
    "id": "mono-53",
    "name": "Topaz Studio",
    "colors": ["#0F0B05", "#1D160A", "#422A14", "#653E1E", "#9A6F52", "#BE967A"]
  },
  {
    "id": "mono-54",
    "name": "Bloom Drift",
    "colors": ["#0C070D", "#170E1A", "#35233E", "#51345F", "#866A9A", "#AD92B8"]
  },
  {
    "id": "mono-55",
    "name": "Iris Ribbon",
    "colors": ["#0F0713", "#1E0E28", "#3F1C58", "#612A88", "#9760BC", "#B889D2"]
  },
  {
    "id": "mono-56",
    "name": "Sable Vault",
    "colors": ["#0A0908", "#141211", "#2E2926", "#48403C", "#7E746E", "#A39B93"]
  },
  {
    "id": "mono-57",
    "name": "Olive Grove",
    "colors": ["#070A05", "#11160B", "#273419", "#3D5126", "#6E8053", "#95A87A"]
  },
  {
    "id": "mono-58",
    "name": "Cobalt Monolith",
    "colors": ["#070915", "#0E1230", "#1E2C6A", "#2E46A4", "#6B79C9", "#98A0DE"]
  },
  {
    "id": "mono-59",
    "name": "Pearl Ladder",
    "colors": ["#0D0D0C", "#191915", "#3B3A33", "#5D5B51", "#9A9788", "#BCB9AB"]
  },
  {
    "id": "mono-60",
    "name": "Crimson Station",
    "colors": ["#140506", "#250A0B", "#54151A", "#842025", "#BA5A5E", "#D58083"]
  },
  {
    "id": "mono-61",
    "name": "Marble Prism",
    "colors": ["#0B0C0D", "#15191A", "#313B3D", "#4D5D60", "#809A9E", "#A5BCBF"]
  },
  {
    "id": "mono-62",
    "name": "Arctic Bridge",
    "colors": ["#060A0E", "#0C141C", "#1A2E41", "#274666", "#57789B", "#7FA0BF"]
  },
  {
    "id": "mono-63",
    "name": "Cinder Station",
    "colors": ["#0B0908", "#171210", "#352A25", "#52423A", "#877468", "#AD9B8E"]
  },
  {
    "id": "mono-64",
    "name": "Lagoon Grove",
    "colors": ["#040B0B", "#071616", "#0E3434", "#155151", "#3B8080", "#61A8A8"]
  },
  {
    "id": "mono-65",
    "name": "Bramble Vault",
    "colors": ["#12070D", "#220E1B", "#4F1E41", "#7A2D63", "#AE6A9B", "#CB91B7"]
  },
  {
    "id": "mono-66",
    "name": "Citrine Canopy",
    "colors": ["#0C0B03", "#171507", "#353014", "#514A20", "#877A4F", "#AB9F73"]
  },
  {
    "id": "mono-67",
    "name": "Raven Station",
    "colors": ["#07070A", "#111018", "#26223A", "#3B345B", "#6E6296", "#9084B1"]
  },
  {
    "id": "mono-68",
    "name": "Juniper Trail",
    "colors": ["#050B08", "#0B1611", "#173427", "#23513D", "#4B7F67", "#71A68E"]
  },
  {
    "id": "mono-69",
    "name": "Copper Studio",
    "colors": ["#100805", "#1F110A", "#482415", "#71371F", "#A86B56", "#C9927D"]
  },
  {
    "id": "mono-70",
    "name": "Nimbus Vault",
    "colors": ["#070A0E", "#0E141E", "#1C2D43", "#2A4767", "#5B7A9C", "#83A2BE"]
  },
  {
    "id": "mono-71",
    "name": "Cedar Grove",
    "colors": ["#0C0805", "#19110A", "#3A2916", "#594123", "#90735A", "#B39A7F"]
  },
  {
    "id": "mono-72",
    "name": "Quartz Echo",
    "colors": ["#0D0C0D", "#19161A", "#3C343E", "#5F5162", "#9B8BA0", "#BDB0C1"]
  },
  {
    "id": "mono-73",
    "name": "Glacier Prism",
    "colors": ["#060A0F", "#0D1420", "#1B2E48", "#294770", "#5979A8", "#80A1CB"]
  },
  {
    "id": "mono-74",
    "name": "Saffron Quarry",
    "colors": ["#0E0A03", "#1E1406", "#4A2F0D", "#754815", "#AF7C3C", "#D4A964"]
  },
  {
    "id": "mono-75",
    "name": "Graphite Station",
    "colors": ["#070809", "#101214", "#23282B", "#363E42", "#636D72", "#879398"]
  },
  {
    "id": "mono-76",
    "name": "Teal Bridge",
    "colors": ["#040B0C", "#0A1619", "#16343A", "#225258", "#4C8086", "#72A8AD"]
  },
  {
    "id": "mono-77",
    "name": "Plum Crown",
    "colors": ["#0E0610", "#1B0B21", "#3B174B", "#5B2375", "#8F58AB", "#AF7FC6"]
  },
  {
    "id": "mono-78",
    "name": "Storm Vault",
    "colors": ["#06080D", "#0D1320", "#1A2A47", "#26406E", "#546FA5", "#7D97C8"]
  },
  {
    "id": "mono-79",
    "name": "Canyon Drift",
    "colors": ["#110705", "#220E0A", "#522114", "#7F311E", "#B56D58", "#D4957D"]
  },
  {
    "id": "mono-80",
    "name": "Petal Ribbon",
    "colors": ["#12070C", "#220E18", "#4F1E3A", "#7A2D5B", "#AE6A93", "#CB91B3"]
  },
  {
    "id": "mono-81",
    "name": "Mint Harbor",
    "colors": ["#050B09", "#0B1613", "#17342C", "#235148", "#4B7F76", "#71A69C"]
  },
  {
    "id": "mono-82",
    "name": "Sepia Station",
    "colors": ["#0E0805", "#1C100B", "#402517", "#633922", "#966E5A", "#B99682"]
  },
  {
    "id": "mono-83",
    "name": "Cocoa Chamber",
    "colors": ["#0F0808", "#1D1010", "#422424", "#653838", "#9A6B6B", "#BE9393"]
  },
  {
    "id": "mono-84",
    "name": "Cerulean Grove",
    "colors": ["#04090F", "#091324", "#133052", "#1D4C7F", "#4D7DB2", "#7AA5D1"]
  },
  {
    "id": "mono-85",
    "name": "Beryl Ribbon",
    "colors": ["#040B08", "#081612", "#103428", "#175141", "#3F7F6A", "#67A792"]
  },
  {
    "id": "mono-86",
    "name": "Wisteria Ladder",
    "colors": ["#0D0810", "#1A1021", "#38224B", "#553474", "#8665A6", "#A88CC2"]
  },
  {
    "id": "mono-87",
    "name": "Tarmac Vault",
    "colors": ["#070808", "#121313", "#282A2A", "#3E4040", "#6E7070", "#949696"]
  },
  {
    "id": "mono-88",
    "name": "Aurora Station",
    "colors": ["#050B08", "#0A1611", "#143425", "#1E513A", "#468064", "#6EA88C"]
  },
  {
    "id": "mono-89",
    "name": "Garnet Prism",
    "colors": ["#120507", "#220A0E", "#4F1521", "#7A2033", "#AE5A6A", "#CB808E"]
  },
  {
    "id": "mono-90",
    "name": "Linen Echo",
    "colors": ["#0D0D0B", "#191915", "#3A3A31", "#5B5B4D", "#989880", "#BABAA5"]
  },
  {
    "id": "mono-91",
    "name": "Fig Vault",
    "colors": ["#0E0810", "#1A1021", "#38224B", "#553474", "#8665A6", "#A88CC2"]
  },
  {
    "id": "mono-92",
    "name": "Silent Studio",
    "colors": ["#070A0E", "#101626", "#223255", "#334E83", "#6D7FB4", "#98A4D1"]
  },
  {
    "id": "mono-93",
    "name": "Topaz Station",
    "colors": ["#0F0B05", "#1D160A", "#422A14", "#653E1E", "#9A6F52", "#BE967A"]
  },
  {
    "id": "mono-94",
    "name": "Bloom Corridor",
    "colors": ["#0C070D", "#170E1A", "#35233E", "#51345F", "#866A9A", "#AD92B8"]
  },
  {
    "id": "mono-95",
    "name": "Iris Drift",
    "colors": ["#0F0713", "#1E0E28", "#3F1C58", "#612A88", "#9760BC", "#B889D2"]
  },
  {
    "id": "mono-96",
    "name": "Sable Bridge",
    "colors": ["#0A0908", "#141211", "#2E2926", "#48403C", "#7E746E", "#A39B93"]
  },
  {
    "id": "mono-97",
    "name": "Olive Prism",
    "colors": ["#070A05", "#11160B", "#273419", "#3D5126", "#6E8053", "#95A87A"]
  },
  {
    "id": "mono-98",
    "name": "Cobalt Ribbon",
    "colors": ["#070915", "#0E1230", "#1E2C6A", "#2E46A4", "#6B79C9", "#98A0DE"]
  },
  {
    "id": "mono-99",
    "name": "Pearl Vault",
    "colors": ["#0D0D0C", "#191915", "#3B3A33", "#5D5B51", "#9A9788", "#BCB9AB"]
  },
  {
    "id": "mono-100",
    "name": "Crimson Crown",
    "colors": ["#140506", "#250A0B", "#54151A", "#842025", "#BA5A5E", "#D58083"]
  },
  {
    "id": "colorful-1",
    "name": "Reef Canopy",
    "colors": ["#150D0D", "#F016A3", "#F83975", "#F30716", "#F39D8A", "#FAF7F8"]
  },
  {
    "id": "colorful-2",
    "name": "Cedar Vista",
    "colors": ["#0E1014", "#2B7BF5", "#2CF2B8", "#F2A72C", "#E0D6F3", "#F7F7FA"]
  },
  {
    "id": "colorful-3",
    "name": "Mist Circuit",
    "colors": ["#0D1214", "#2BE6F5", "#2CF29E", "#2B52F5", "#B9F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-4",
    "name": "Basil Runway",
    "colors": ["#0D1310", "#2CF556", "#F5C32B", "#F52B9D", "#9CF3B8", "#F8FAF7"]
  },
  {
    "id": "colorful-5",
    "name": "Quartz Station",
    "colors": ["#121018", "#8A2BF5", "#F52B7F", "#2BF5B8", "#F3B49C", "#FAF8FB"]
  },
  {
    "id": "colorful-6",
    "name": "Cobalt Arcade",
    "colors": ["#0D1015", "#2B49F5", "#2BF5E6", "#F52B3E", "#9CD1F3", "#F7F9FA"]
  },
  {
    "id": "colorful-7",
    "name": "Honey Harbor",
    "colors": ["#14120D", "#F5B62B", "#2BF56B", "#2B61F5", "#F3D99C", "#FBFAF7"]
  },
  {
    "id": "colorful-8",
    "name": "Orchid Avenue",
    "colors": ["#151016", "#F52BCE", "#8B2BF5", "#2BF56B", "#F3C39C", "#FAF7FB"]
  },
  {
    "id": "colorful-9",
    "name": "Aurora Market",
    "colors": ["#0E1312", "#2BF59A", "#2B8CF5", "#F52B5C", "#B9F3D8", "#F7FAF9"]
  },
  {
    "id": "colorful-10",
    "name": "Citrine Theater",
    "colors": ["#14120B", "#F5DA2B", "#2BF5B0", "#2B4FF5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-11",
    "name": "Velvet Bazaar",
    "colors": ["#151017", "#F52B8C", "#2BF5DA", "#2B5CF5", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-12",
    "name": "Glacier Boulevard",
    "colors": ["#0D1115", "#2BBBF5", "#2BF5E6", "#F52B5C", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-13",
    "name": "Ember Orchard",
    "colors": ["#17110E", "#F56B2B", "#2BF5A6", "#2B49F5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-14",
    "name": "Slate Promenade",
    "colors": ["#0F1116", "#2B5CF5", "#2BF5B0", "#F5A72B", "#C7D2F3", "#F7F9FA"]
  },
  {
    "id": "colorful-15",
    "name": "Berry Cinema",
    "colors": ["#160E14", "#F52B7F", "#2BF5CE", "#2B49F5", "#F3B49C", "#FAF7FA"]
  },
  {
    "id": "colorful-16",
    "name": "Marble Parade",
    "colors": ["#0F1214", "#2B8CF5", "#2BF5B8", "#F52BCE", "#D6E0F3", "#F7FAFA"]
  },
  {
    "id": "colorful-17",
    "name": "Cinder Pavilion",
    "colors": ["#17120F", "#F56B2B", "#2BF556", "#2B61F5", "#F3D99C", "#FBF8F7"]
  },
  {
    "id": "colorful-18",
    "name": "Juniper Plaza",
    "colors": ["#0E1310", "#2BF56B", "#2BBBF5", "#F52B7F", "#B9F3C3", "#F7FAF7"]
  },
  {
    "id": "colorful-19",
    "name": "Nimbus Diner",
    "colors": ["#101216", "#2B61F5", "#2BF5E6", "#F5DA2B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-20",
    "name": "Topaz Backstage",
    "colors": ["#14110C", "#F5A72B", "#2BF59A", "#2B4FF5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-21",
    "name": "Arctic Skyline",
    "colors": ["#0D1115", "#2BBBF5", "#8A2BF5", "#F52B5C", "#C3D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-22",
    "name": "Copper Avenue",
    "colors": ["#17110B", "#F58C2B", "#2BF5B8", "#2B61F5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-23",
    "name": "Wisteria Market",
    "colors": ["#121018", "#8B2BF5", "#2B8CF5", "#F52B9D", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-24",
    "name": "Saffron Arcade",
    "colors": ["#14120B", "#F5C32B", "#2BF56B", "#2B49F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-25",
    "name": "Lagoon Boulevard",
    "colors": ["#0D1314", "#2BF5DA", "#2BBBF5", "#F52B7F", "#B9F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-26",
    "name": "Garnet Cinema",
    "colors": ["#160D10", "#F52B3E", "#F52BCE", "#2B61F5", "#F39C9C", "#FAF7F8"]
  },
  {
    "id": "colorful-27",
    "name": "Olive Promenade",
    "colors": ["#12130D", "#2BF56B", "#F5DA2B", "#2B8CF5", "#D8F3B9", "#FAFAF7"]
  },
  {
    "id": "colorful-28",
    "name": "Cerulean Parade",
    "colors": ["#0D1015", "#2B8CF5", "#2BF5E6", "#F56B2B", "#9CD2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-29",
    "name": "Pearl Diner",
    "colors": ["#111214", "#2B61F5", "#2BF5B8", "#F5C32B", "#E0E0F3", "#F9FAFA"]
  },
  {
    "id": "colorful-30",
    "name": "Storm Pavilion",
    "colors": ["#0E1116", "#2B49F5", "#8A2BF5", "#2BF5DA", "#C7B9F3", "#F7F7FA"]
  },
  {
    "id": "colorful-31",
    "name": "Canyon Theater",
    "colors": ["#17110D", "#F56B2B", "#F5DA2B", "#2BF59A", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-32",
    "name": "Petal Orchard",
    "colors": ["#150E15", "#F52B9D", "#F52B7F", "#2BBBF5", "#F3B9D6", "#FAF7FA"]
  },
  {
    "id": "colorful-33",
    "name": "Mint Bazaar",
    "colors": ["#0D1312", "#2BF59A", "#2BF5E6", "#8B2BF5", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-34",
    "name": "Sepia Boardwalk",
    "colors": ["#15110D", "#F58C2B", "#F5C32B", "#2B49F5", "#F3D99C", "#FBF8F7"]
  },
  {
    "id": "colorful-35",
    "name": "Cocoa Skyline",
    "colors": ["#170F12", "#F52B5C", "#F52BCE", "#2B8CF5", "#F3B49C", "#FAF7F8"]
  },
  {
    "id": "colorful-36",
    "name": "Sable Plaza",
    "colors": ["#111113", "#2B5CF5", "#2BF5B0", "#F5DA2B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-37",
    "name": "Iris Market",
    "colors": ["#121018", "#8A2BF5", "#2BF5DA", "#F56B2B", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-38",
    "name": "Bramble Station",
    "colors": ["#150E14", "#F52B7F", "#8B2BF5", "#2BF59A", "#D6B9F3", "#FAF7FA"]
  },
  {
    "id": "colorful-39",
    "name": "Marble Market",
    "colors": ["#0F1214", "#2B8CF5", "#2BF5B8", "#F52BCE", "#D6E0F3", "#F7FAFA"]
  },
  {
    "id": "colorful-40",
    "name": "Copper Grove",
    "colors": ["#17110B", "#F58C2B", "#2BF56B", "#2B4FF5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-41",
    "name": "Glacier Circuit",
    "colors": ["#0D1115", "#2BBBF5", "#2BF5E6", "#F52B7F", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-42",
    "name": "Citrine Harbor",
    "colors": ["#14110B", "#F5DA2B", "#2BF59A", "#2B49F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-43",
    "name": "Velvet Cinema",
    "colors": ["#151017", "#F52B8C", "#2B8CF5", "#2BF5B0", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-44",
    "name": "Lagoon Theater",
    "colors": ["#0D1314", "#2BF5DA", "#2BBBF5", "#F56B2B", "#B9F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-45",
    "name": "Saffron Boulevard",
    "colors": ["#14120B", "#F5C32B", "#2BF56B", "#2B61F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-46",
    "name": "Wisteria Arcade",
    "colors": ["#121018", "#8B2BF5", "#2BF5CE", "#F52B9D", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-47",
    "name": "Storm Bazaar",
    "colors": ["#0E1116", "#2B49F5", "#2BF5E6", "#F5A72B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-48",
    "name": "Canyon Parade",
    "colors": ["#17110D", "#F56B2B", "#2BF59A", "#2B4FF5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-49",
    "name": "Petal Harbor",
    "colors": ["#150E15", "#F52B9D", "#2BBBF5", "#F5DA2B", "#F3B9D6", "#FAF7FA"]
  },
  {
    "id": "colorful-50",
    "name": "Mint Cinema",
    "colors": ["#0D1312", "#2BF59A", "#2BF5E6", "#F52B7F", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-51",
    "name": "Sepia Backstage",
    "colors": ["#15110D", "#F58C2B", "#2BF56B", "#2B8CF5", "#F3D99C", "#FBF8F7"]
  },
  {
    "id": "colorful-52",
    "name": "Cocoa Boardwalk",
    "colors": ["#170F12", "#F52B5C", "#2B61F5", "#2BF5B0", "#F3B49C", "#FAF7F8"]
  },
  {
    "id": "colorful-53",
    "name": "Sable Boulevard",
    "colors": ["#111113", "#2B5CF5", "#2BF5DA", "#F5C32B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-54",
    "name": "Iris Skyline",
    "colors": ["#121018", "#8A2BF5", "#2B8CF5", "#F56B2B", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-55",
    "name": "Bramble Theater",
    "colors": ["#150E14", "#F52B7F", "#8B2BF5", "#2BF5DA", "#D6B9F3", "#FAF7FA"]
  },
  {
    "id": "colorful-56",
    "name": "Marble Studio",
    "colors": ["#0F1214", "#2B8CF5", "#2BF5B8", "#F5A72B", "#D6E0F3", "#F7FAFA"]
  },
  {
    "id": "colorful-57",
    "name": "Copper Harbor",
    "colors": ["#17110B", "#F58C2B", "#2BF56B", "#2B49F5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-58",
    "name": "Glacier Parade",
    "colors": ["#0D1115", "#2BBBF5", "#2BF5E6", "#F5DA2B", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-59",
    "name": "Citrine Cinema",
    "colors": ["#14110B", "#F5DA2B", "#2BF59A", "#2B8CF5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-60",
    "name": "Velvet Orchard",
    "colors": ["#151017", "#F52B8C", "#2BF5B0", "#2B61F5", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-61",
    "name": "Lagoon Boardwalk",
    "colors": ["#0D1314", "#2BF5DA", "#2BBBF5", "#F52B7F", "#B9F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-62",
    "name": "Saffron Skyline",
    "colors": ["#14120B", "#F5C32B", "#2BF56B", "#2B4FF5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-63",
    "name": "Wisteria Boulevard",
    "colors": ["#121018", "#8B2BF5", "#2BF5CE", "#F56B2B", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-64",
    "name": "Storm Market",
    "colors": ["#0E1116", "#2B49F5", "#2BF5E6", "#F52B5C", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-65",
    "name": "Canyon Studio",
    "colors": ["#17110D", "#F56B2B", "#2BF59A", "#2B61F5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-66",
    "name": "Petal Bazaar",
    "colors": ["#150E15", "#F52B9D", "#2BBBF5", "#F5DA2B", "#F3B9D6", "#FAF7FA"]
  },
  {
    "id": "colorful-67",
    "name": "Mint Theater",
    "colors": ["#0D1312", "#2BF59A", "#2BF5E6", "#8A2BF5", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-68",
    "name": "Sepia Market",
    "colors": ["#15110D", "#F58C2B", "#F5C32B", "#2B8CF5", "#F3D99C", "#FBF8F7"]
  },
  {
    "id": "colorful-69",
    "name": "Cocoa Arcade",
    "colors": ["#170F12", "#F52B5C", "#2B61F5", "#2BF5B0", "#F3B49C", "#FAF7F8"]
  },
  {
    "id": "colorful-70",
    "name": "Sable Harbor",
    "colors": ["#111113", "#2B5CF5", "#2BF5DA", "#F5DA2B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-71",
    "name": "Iris Pavilion",
    "colors": ["#121018", "#8A2BF5", "#2B8CF5", "#F52B7F", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-72",
    "name": "Bramble Boulevard",
    "colors": ["#150E14", "#F52B7F", "#8B2BF5", "#2BF5B8", "#D6B9F3", "#FAF7FA"]
  },
  {
    "id": "colorful-73",
    "name": "Marble Market",
    "colors": ["#0F1214", "#2B8CF5", "#2BF5B8", "#F5C32B", "#D6E0F3", "#F7FAFA"]
  },
  {
    "id": "colorful-74",
    "name": "Copper Theater",
    "colors": ["#17110B", "#F58C2B", "#2BF56B", "#2BBBF5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-75",
    "name": "Glacier Bazaar",
    "colors": ["#0D1115", "#2BBBF5", "#2BF5E6", "#F56B2B", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-76",
    "name": "Citrine Parade",
    "colors": ["#14110B", "#F5DA2B", "#2BF59A", "#2B49F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-77",
    "name": "Velvet Harbor",
    "colors": ["#151017", "#F52B8C", "#2BF5B0", "#2B8CF5", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-78",
    "name": "Lagoon Cinema",
    "colors": ["#0D1314", "#2BF5DA", "#2BBBF5", "#F52B9D", "#B9F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-79",
    "name": "Saffron Theater",
    "colors": ["#14120B", "#F5C32B", "#2BF56B", "#2B61F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-80",
    "name": "Wisteria Harbor",
    "colors": ["#121018", "#8B2BF5", "#2BF5CE", "#F52B5C", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-81",
    "name": "Storm Arcade",
    "colors": ["#0E1116", "#2B49F5", "#2BF5E6", "#F5A72B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-82",
    "name": "Canyon Harbor",
    "colors": ["#17110D", "#F56B2B", "#F5DA2B", "#2B8CF5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-83",
    "name": "Petal Parade",
    "colors": ["#150E15", "#F52B9D", "#2BBBF5", "#F5C32B", "#F3B9D6", "#FAF7FA"]
  },
  {
    "id": "colorful-84",
    "name": "Mint Cinema",
    "colors": ["#0D1312", "#2BF59A", "#2BF5E6", "#F56B2B", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-85",
    "name": "Sepia Theater",
    "colors": ["#15110D", "#F58C2B", "#F5C32B", "#2B49F5", "#F3D99C", "#FBF8F7"]
  },
  {
    "id": "colorful-86",
    "name": "Cocoa Market",
    "colors": ["#170F12", "#F52B5C", "#2B61F5", "#2BF5DA", "#F3B49C", "#FAF7F8"]
  },
  {
    "id": "colorful-87",
    "name": "Sable Cinema",
    "colors": ["#111113", "#2B5CF5", "#2BF5B0", "#F5DA2B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-88",
    "name": "Iris Boulevard",
    "colors": ["#121018", "#8A2BF5", "#2B8CF5", "#F52B7F", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-89",
    "name": "Bramble Arcade",
    "colors": ["#150E14", "#F52B7F", "#8B2BF5", "#2BF5B8", "#D6B9F3", "#FAF7FA"]
  },
  {
    "id": "colorful-90",
    "name": "Marble Skyline",
    "colors": ["#0F1214", "#2B8CF5", "#2BF5B8", "#F56B2B", "#D6E0F3", "#F7FAFA"]
  },
  {
    "id": "colorful-91",
    "name": "Copper Bazaar",
    "colors": ["#17110B", "#F58C2B", "#2BF56B", "#2BBBF5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-92",
    "name": "Glacier Market",
    "colors": ["#0D1115", "#2BBBF5", "#2BF5E6", "#F52B5C", "#C3F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-93",
    "name": "Citrine Studio",
    "colors": ["#14110B", "#F5DA2B", "#2BF59A", "#2B49F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-94",
    "name": "Velvet Theater",
    "colors": ["#151017", "#F52B8C", "#2BF5B0", "#2B8CF5", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-95",
    "name": "Lagoon Pavilion",
    "colors": ["#0D1314", "#2BF5DA", "#2BBBF5", "#F52B7F", "#B9F3E0", "#F7FAFA"]
  },
  {
    "id": "colorful-96",
    "name": "Saffron Market",
    "colors": ["#14120B", "#F5C32B", "#2BF56B", "#2B61F5", "#F3E29C", "#FBFAF7"]
  },
  {
    "id": "colorful-97",
    "name": "Wisteria Parade",
    "colors": ["#121018", "#8B2BF5", "#2BF5CE", "#F56B2B", "#D6B9F3", "#FAF7FB"]
  },
  {
    "id": "colorful-98",
    "name": "Storm Theater",
    "colors": ["#0E1116", "#2B49F5", "#2BF5E6", "#F5A72B", "#C7D2F3", "#F7FAFA"]
  },
  {
    "id": "colorful-99",
    "name": "Canyon Bazaar",
    "colors": ["#17110D", "#F56B2B", "#F5DA2B", "#2B8CF5", "#F3C39C", "#FBF8F7"]
  },
  {
    "id": "colorful-100",
    "name": "Petal Market",
    "colors": ["#150E15", "#F52B9D", "#2BBBF5", "#F5C32B", "#F3B9D6", "#FAF7FA"]
  }
]

const currentTemplate = computed(() =>
  templates.find(t => t.id === selectedTemplateId.value) || null
)

function colorsLimitForTemplate(templateId) {
  if (templateId === 'duo' || templateId === 'duo-inverted' || templateId === 'hero-label') return 2
  if (templateId === 'triple-stripe' || templateId === 'split-banner' || templateId === 'gradient-strip' || templateId === 'spotlight' || templateId === 'photo-card-stack') return 3
  if (templateId === 'quadrant' || templateId === 'side-label' || templateId === 'corner-accent' || templateId === 'contrast-blocks' || templateId === 'timeline') return 4
  if (templateId === 'card-stack' || templateId === 'neon-grid' || templateId === 'linear-bars' || templateId === 'mega-columns' || templateId === 'pill-row' || templateId === 'badge-cluster' || templateId === 'center-burst') return 5
  if (templateId === 'tag-grid' || templateId === 'masonry') return 6
  return 4
}

function previewColorsForTemplate(templateId) {
  const limit = colorsLimitForTemplate(templateId)
  return colors.value.slice(0, limit).map((c, index) => ({
    ...c,
    roleLabel: roleLabelForTemplate(templateId, index)
  }))
}

function ensureColorsForTemplate(templateId) {
  const limit = colorsLimitForTemplate(templateId)
  while (colors.value.length < limit) {
    addColor()
  }
}

function openTemplateModal() {
  isTemplateModalOpen.value = true
}

function closeTemplateModal() {
  isTemplateModalOpen.value = false
}

function openPaletteModal() {
  isPaletteModalOpen.value = true
}

function closePaletteModal() {
  isPaletteModalOpen.value = false
}

function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `c-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function addColor() {
  const nextIndex = colors.value.length
  const defaultHex = '#000000'
  colors.value.push({
    id: generateId(),
    name: generateColorName(defaultHex),
    hex: defaultHex,
    position: nextIndex
  })
}

function removeColor(id) {
  colors.value = colors.value.filter(c => c.id !== id)
  colors.value = colors.value.map((c, index) => ({
    ...c,
    position: index
  }))
}

function chooseTemplate(id) {
  selectedTemplateId.value = id
  ensureColorsForTemplate(id)
  isTemplateModalOpen.value = false
}

function inferTemplateForColorsCount(count) {
  if (count === 2) return 'duo'
  if (count === 3) return 'triple-stripe'
  if (count === 4) return 'quadrant'
  return 'card-stack'
}

function applyPaletteCombo(combo) {
  colors.value = combo.colors.map((hex, index) => ({
    id: generateId(),
    name: generateColorName(hex),
    hex: normalizeHex(hex),
    position: index
  }))
  if (!selectedTemplateId.value) {
    selectedTemplateId.value = inferTemplateForColorsCount(combo.colors.length)
  }
  ensureColorsForTemplate(selectedTemplateId.value)
  isPaletteModalOpen.value = false
}

async function waitForExportAssets(node) {
  const pending = []

  node.querySelectorAll('img').forEach(img => {
    if (img.complete && img.naturalWidth > 0) return
    pending.push(new Promise(resolve => {
      const done = () => resolve()
      img.addEventListener('load', done, { once: true })
      img.addEventListener('error', done, { once: true })
    }))
  })

  const backgroundUrls = new Set()
  const elements = [node, ...node.querySelectorAll('*')]
  const urlRegex = /url\(["']?([^"')]+)["']?\)/g

  elements.forEach(el => {
    const backgroundImage = window.getComputedStyle(el).backgroundImage || ''
    let match
    while ((match = urlRegex.exec(backgroundImage)) !== null) {
      if (match[1] && !match[1].startsWith('data:')) {
        backgroundUrls.add(match[1])
      }
    }
  })

  backgroundUrls.forEach(src => {
    pending.push(new Promise(resolve => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => resolve()
      img.onerror = () => resolve()
      img.src = src
    }))
  })

  if (pending.length) {
    await Promise.all(pending)
  }
}

function downsampleExportCanvas(sourceCanvas, targetWidth, targetHeight) {
  if (sourceCanvas.width === targetWidth && sourceCanvas.height === targetHeight) {
    return sourceCanvas
  }

  const output = document.createElement('canvas')
  output.width = targetWidth
  output.height = targetHeight
  const ctx = output.getContext('2d')
  if (!ctx) return sourceCanvas

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.clearRect(0, 0, targetWidth, targetHeight)
  ctx.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight)
  return output
}

async function captureExportCanvas() {
  const node = exportRef.value
  if (!node || !currentTemplate.value) {
    throw new Error('No export node or template for PNG')
  }

  const targetWidth = EXPORT_WIDTH
  const targetHeight = exportHeight.value

  if (currentTemplate.value.id === 'photo-card-stack') {
    photoStackExportBackground.value = await createPhotoStackExportBackground(
      previewColorsForTemplate('photo-card-stack')
    )
  } else {
    photoStackExportBackground.value = ''
  }

  await nextTick()
  if (document.fonts?.ready) {
    await document.fonts.ready
  }
  await waitForExportAssets(node)
  node.getBoundingClientRect()

  await new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })

  const hiResCanvas = await html2canvas(node, {
    backgroundColor: null,
    scale: 2,
    width: targetWidth,
    height: targetHeight,
    useCORS: true,
    allowTaint: false,
    logging: false,
    removeContainer: true,
    foreignObjectRendering: false,
    imageTimeout: 15000,
    scrollX: 0,
    scrollY: 0,
    windowWidth: targetWidth,
    windowHeight: targetHeight,
    onclone: clonedDocument => {
      const clonedRoot = clonedDocument.querySelector('.export-canvas')
      if (!clonedRoot) return

      clonedRoot.querySelectorAll('*').forEach(el => {
        el.style.animation = 'none'
        el.style.transition = 'none'
        el.style.caretColor = 'transparent'
      })
    }
  })

  return downsampleExportCanvas(hiResCanvas, targetWidth, targetHeight)
}

async function exportPalettePng() {
  if (!currentTemplate.value) return

  const safeName = (project.name || 'palette')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'palette'

  try {
    const canvas = await captureExportCanvas()
    const dataUrl = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = `${safeName}-${EXPORT_WIDTH}x${exportHeight.value}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (e) {
    console.error('Error exporting palette PNG', e)
  }
}

async function fetchProject() {
  const projectId = route.params.projectId
  if (!projectId) {
    project.id = null
    project.name = 'Neon Noir Experiments'
    project.description = 'Exploring neon + dark combinations for covers and thumbnails.'
    if (!colors.value.length) {
      const initial = ['#060761', '#41FFA7', '#C4FF4D', '#BA8CFF', '#111111']
      colors.value = initial.map((hex, index) => ({
        id: generateId(),
        name: generateColorName(hex),
        hex: normalizeHex(hex),
        position: index
      }))
    }
    return
  }

  try {
    isLoading.value = true
    const { data } = await axios.get(`/studio/projects/${projectId}`, {
      headers: authService.authHeaders()
    })
    if (data) {
      project.id = data.id
      project.name = data.name
      project.description = data.description || ''
      colors.value = (data.colors || [])
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((c, index) => {
          const hex = normalizeHex(c.hex || '#000000')
          return {
            id: c.id || generateId(),
            name: c.name && c.name.trim() ? c.name : generateColorName(hex),
            hex,
            position: typeof c.position === 'number' ? c.position : index
          }
        })
      if (!colors.value.length) {
        const initial = ['#060761', '#41FFA7', '#C4FF4D', '#BA8CFF', '#111111']
        colors.value = initial.map((hex, index) => ({
          id: generateId(),
          name: generateColorName(hex),
          hex: normalizeHex(hex),
          position: index
        }))
      }
      if (data.template && enumToTemplateId[data.template]) {
        selectedTemplateId.value = enumToTemplateId[data.template]
        ensureColorsForTemplate(selectedTemplateId.value)
      } else {
        selectedTemplateId.value = null
      }
    }
  } catch (e) {
    console.error('Error loading project', e)
    project.id = null
  } finally {
    isLoading.value = false
  }
}

async function saveProject() {
  if (isSaving.value) return

  try {
    isSaving.value = true
    let projectId = project.id

    if (!projectId) {
      const createPayload = {
        name: project.name || 'Untitled palette',
        description: project.description || ''
      }

      const resCreate = await axios.post('/studio/projects', createPayload, {
        headers: authService.authHeaders()
      })

      projectId = resCreate.data.id
      project.id = resCreate.data.id

      router.replace({
        name: 'studio-project',
        params: { projectId }
      })
    }

    const templateEnum = selectedTemplateId.value
      ? templateIdToEnum[selectedTemplateId.value]
      : undefined

    const payload = {
      template: templateEnum,
      colors: colors.value.map((c, index) => ({
        name: c.name && c.name.trim() ? c.name : generateColorName(c.hex),
        hex: normalizeHex(c.hex),
        position: typeof c.position === 'number' ? c.position : index
      }))
    }

    const { data } = await axios.patch(
      `/studio/projects/${projectId}/colors`,
      payload,
      { headers: authService.authHeaders() }
    )

    if (data) {
      colors.value = (data.colors || [])
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((c, index) => {
          const hex = normalizeHex(c.hex || '#000000')
          return {
            id: c.id || generateId(),
            name: c.name && c.name.trim() ? c.name : generateColorName(hex),
            hex,
            position: typeof c.position === 'number' ? c.position : index
          }
        })
      if (data.template && enumToTemplateId[data.template]) {
        selectedTemplateId.value = enumToTemplateId[data.template]
        ensureColorsForTemplate(selectedTemplateId.value)
      }
      project.name = data.name
      project.description = data.description || ''
    }
  } catch (e) {
    console.error('Error saving project', e)
  } finally {
    isSaving.value = false
  }
}

async function loadFolders() {
  try {
    isFoldersLoading.value = true
    const { data } = await axios.get('/images/groups', {
      headers: authService.authHeaders()
    })

    const groups = Array.isArray(data?.groups) ? data.groups : []

    folders.value = groups.map(g => ({
      id: g.name,
      name: g.name,
      description: '',
      itemsCount: g.count,
      cover: g.cover
    }))
  } catch (e) {
    console.error('Error loading folders', e)
    folders.value = []
  } finally {
    isFoldersLoading.value = false
  }
}

async function saveAndOpenFolderModal() {
  try {
    await saveProject()

    selectedFolderId.value = null
    await loadFolders()
    isFolderModalOpen.value = true
  } catch (e) {
    console.error('Error opening folder modal', e)
  }
}

function getSafeProjectSlug() {
  const base = project.name || 'palette'
  return base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'palette'
}

function closeFolderModal() {
  isFolderModalOpen.value = false
}

async function confirmSaveToFolder() {
  if (!project.id || !selectedFolderId.value || isSavingToFolder.value) return

  try {
    isSavingToFolder.value = true

    await saveProject()

    const node = exportRef.value
    if (!node || !currentTemplate.value) {
      console.error('No export node or template for PNG')
      return
    }

    const slug = getSafeProjectSlug()

    const canvas = await captureExportCanvas()

    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (blob) resolve(blob)
          else reject(new Error('Failed to create PNG blob'))
        },
        'image/png',
        1
      )
    })

    const formData = new FormData()
    formData.append('files', pngBlob, `${slug}-${EXPORT_WIDTH}x${exportHeight.value}.png`)

    await axios.post(
      `/images/upload?group=${encodeURIComponent(selectedFolderId.value)}`,
      formData,
      {
        headers: {
          ...authService.authHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    isFolderModalOpen.value = false
  } catch (e) {
    console.error('Error saving project to folder', e)
  } finally {
    isSavingToFolder.value = false
  }
}

function goBack() {
  router.push({ name: 'studio' })
}

onMounted(() => {
  const current = authService.current()
  if (!current) {
    router.push('/')
    return
  }
  fetchProject()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Roboto+Condensed:wght@700;800;900&display=swap');

.custom-scroll::-webkit-scrollbar {
  width: 4px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(161, 161, 170, 0.3);
  border-radius: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(161, 161, 170, 0.5);
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.list-leave-active {
  position: absolute;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in-down {
  animation: fadeInDown 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.animate-scale-in {
  animation: scaleIn 0.3s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.export-typography {
  font-family: "Bebas Neue", "Impact", system-ui, -apple-system, BlinkMacSystemFont,
    "Segoe UI", sans-serif;
  letter-spacing: 0.04em;
}

.export-typography .font-mono {
  font-family: "Space Mono", "JetBrains Mono", ui-monospace, SFMono-Regular,
    Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  letter-spacing: 0.08em;
}

.export-typography [class*="tracking-[0.2"] {
  letter-spacing: 0.26em;
}

.export-typography .text-6xl,
.export-typography .text-7xl {
  line-height: 0.95;
}

.export-typography .export-stack {
  gap: 0.75rem;
}

.export-canvas,
.export-canvas * {
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricPrecision;
}

.export-canvas {
  isolation: isolate;
}
</style>
